import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
/** «Статус» — rolling-сводка отклонений филиала (Алексей 24.09, AI182/AI183).
 *
 *  Вопрос раздела: «Какие топ проблемы с качеством сейчас? Над чем работать?».
 *  Отчёт дня/недели отвечает за отчётный период, Статистика — за любой вопрос,
 *  а здесь берём рекомендованный период (14 полных дней, сегодня не считаем),
 *  собираем проверенные вердикты по дням (тот же источник, что у отчёта дня —
 *  day-findings) и выносим наверх проблемное, вниз — свёрнутое «всё хорошо».
 *  Динамика — против предыдущих 14 дней, обязательна (#AI183).
 *
 *  Переделка 12.10 (Алексей #1438, AI252): всё actionable — в каждой панели кнопка
 *  «В статистике», замечания конкретными строками (что именно не так, почему),
 *  четыре уровня «заметная проблема / проблема / небольшая проблема / недочёт»
 *  мягкими цветами, выкладка — рядами по критериям (открытие / просадки /
 *  остатки на закрытии) со стеллажами и фото внутри, речь — конкретные проблемы
 *  с «послушать», чек-листы — когда должна была выполниться и «изменить».
 *  Часы, не проценты (владелец: «намеренно ставим часы»). */
import { getOrderPlan, type OrderPlanDto } from '@/api/orderPlan';
import { OrderPlanBlock } from '@/components/orderPlan/OrderPlanBlock';
import { ORDER_PLAN_READY } from '@/components/orderPlan/OrderPlanTab';
import { money } from '@/lib/finance';
import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import { fmtPersonTime } from '@/utils/personTime';
import { ChevronDown, Loader2, ArrowDown, ArrowDownRight, ArrowUp, ArrowUpRight, ExternalLink, Users, UtensilsCrossed, ListTodo, Sunrise, MessageSquareText, Star, ClipboardCheck, Wifi, BarChart3, Info, Pencil, Settings2, TrendingUp, Receipt as ReceiptIcon, Clock, UsersRound, RotateCcw, Wallet, CalendarDays, Link2, ShoppingBasket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useT } from '@/contexts/I18nContext';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import { getReceiptsSummary, type ReceiptsSummaryDto } from '@/api/receipts';
import type { FeatureKey } from '@/api/features';
import { StoryConnector, StoryFrame, TableStory, useTableCorrection, type TableCut, type TableGroup } from '@/components/report/day/Kadry';
import { useCan } from '@/hooks/useCan';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { cn } from '@/lib/utils';
import { getDataLinks, type DataLinkDto } from '@/api/dataLinks';
import { getLayoutDips, getQueueDetail, getQueueEpisodes, getQueueShortEpisodes, getQueueWorkerExamples, getStaffArrival, getStatusWindow, type StaffArrivalDayDto, type StaffArrivalResponse, type DayFindingsResponse, type DipEpisode, type FindingsPhoto, type LayoutDipsResponse, type QueueEpisode, type QueueEpisodesResponse, type QueueWorkerMatrixCell } from '@/api/analytics';
import { isProdHost } from '@/lib/host';
import { StaffArrivalSummaryCard } from '@/components/report/day/StaffArrivalCard';
import { getDailyReports, type DailyReportDto, type DailyReportPhoto, type TableIncidentCorrection } from '@/api/dailyReports';
import { getConversationsOverview, type ConversationsOverview, type OverviewBar, type OverviewProblem } from '@/api/conversations';
import { getReviewsStats, type ReviewsStatsDto, type ReviewDto } from '@/api/reviews';
import { getVisit, listZoneVisits, type VisitSummaryDto, type VisitDetailDto } from '@/api/visits';
import { getHoursChecks, type HoursChecksDto, type SiteHoursRow } from '@/api/brands';
import { getWeekdayLabels, maskBits } from '@/components/working-hours/weekdays';
import { CATEGORY_LABEL } from './AnalyticsReviewsTab';
import { ClipExample } from './ConversationsOverview';
import { ChecklistItemReports } from './AnalyticsPage';

export const STATUS_DAYS = 14;
export const RARE_DAYS = 90;   // отзывы и визиты — редкие события, как в Статистике
// Чеки и возвраты — за 4 полные недели против прошлых 4 (Алексей 25.10 #2395: «не за 14 дней, а за месяц»):
// за 2 недели средний чек скачет от праздников и погоды, 28 дней — поровну будней и выходных.
export const RECEIPTS_DAYS = 28;

/** Уровни (Алексей 12.10, #1438 п.6 и дополнение): «Плохо» не пишем. */
export type Light = 'red' | 'orange' | 'yellow' | 'lime' | 'green' | 'grey';
export const LIGHT_ORDER: Record<Light, number> = { red: 0, orange: 1, yellow: 2, lime: 3, green: 4, grey: 5 };
// Цвета — шкала системы, как точки дашборда смены (Алексей 23.10, #2065: «зелёный не такой
// голубой… посерьёзнее, не детский сад»): зелёный = bg-success, без пастели.
export const LIGHT_DOT: Record<Light, string> = {
  red: 'bg-red-600', orange: 'bg-orange-500', yellow: 'bg-amber-400', lime: 'bg-yellow-300', green: 'bg-success', grey: 'bg-muted-foreground/40',
};
export const LIGHT_LABEL: Record<Light, string> = {
  red: 'Заметная проблема', orange: 'Проблема', yellow: 'Небольшая проблема', lime: 'Недочёт', green: 'В норме', grey: 'Нет данных',
};
export const isProblem = (l: Light) => l === 'red' || l === 'orange' || l === 'yellow' || l === 'lime';
const worstOf = (lights: Light[], base: Light = 'green'): Light => lights.reduce<Light>((w, l) => (LIGHT_ORDER[l] < LIGHT_ORDER[w] ? l : w), base);

export function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
/** Только дата: на вход может прийти полный ISO-штамп («…T15:22:57Z» рвался на «05T15…», #1438). */
function fmtDay(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}`;
}
function fmtHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} мин`;
  return `${h.toFixed(h >= 10 ? 0 : 1).replace('.0', '')} ч`;
}
function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10; const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
function hhmm(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
}
const statsRange = (days: number, toAgo = 1) => `from=${isoDaysAgo(days)}&to=${isoDaysAgo(toAgo)}`;
const STATS: Record<string, string> = {
  layout: `/management/analytics?tab=layout&${statsRange(STATUS_DAYS)}`,
  queue: `/management/analytics?tab=queue&${statsRange(STATUS_DAYS)}`,
  tables: `/management/analytics?tab=tables&${statsRange(STATUS_DAYS)}`,
  tasks: `/management/analytics?tab=tasks&${statsRange(STATUS_DAYS)}`,
  speech: `/management/analytics?tab=conversations&${statsRange(STATUS_DAYS)}`,
  reviews: `/management/analytics?tab=reviews&${statsRange(RARE_DAYS, 0)}`,
  visits: '/management/analytics?tab=visits',
  order_plan: `/management/analytics?tab=order_plan&${statsRange(1, 0)}`,
};

export type DayRow = { day: string; data: DayFindingsResponse | null };

/** Окно дней одним запросом (снимки на BE); день без данных — null. */
function useWindow(zoneId: string, fromAgo: number, toAgo: number, on = true) {
  const days = fromAgo - toAgo + 1;
  return useSWR<DayRow[]>(
    on ? ['analytics:status:window', zoneId, days, toAgo] : null,
    () => getStatusWindow(zoneId, days, toAgo).then((r) => r.data.days.map((d) => ({ day: d.day, data: d.findings }))),
    { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 },
  );
}

// ── Отчёты дня: фото-доказательства и эпизоды столов ─────────────────────────
// Отчёт дня уже хранит подписанные моменты (На открытии / Самая большая просадка /
// Перед закрытием, очередь, эпизоды грязных столов с минутами) — берём их как
// конкретные примеры, а не перцентили (владелец 24.09, AI183).

function useReports(zoneId: string, fromAgo: number, toAgo: number, on = true) {
  const from = isoDaysAgo(fromAgo); const to = isoDaysAgo(toAgo);
  return useSWR<DailyReportDto[]>(
    on ? ['analytics:status:reports', zoneId, from, to] : null,
    () => getDailyReports(zoneId, from, to, 1).then((p) => p.results).catch(() => []),
    { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 },
  );
}

/** `reportId`/`tableId`/`stretchStart` — чтобы исправить эпизод отсюда так же, как в отчёте дня (Алексей 21.10). */
type TableEpisode = { day: string; table: string; startMs: number; endMs: number; minutes: number; photos: DailyReportPhoto[]; reportId: number; tableId?: number; stretchStart?: string };

function tableEpisodes(reports: DailyReportDto[]): TableEpisode[] {
  const map = new Map<string, TableEpisode>();
  for (const r of reports) {
    for (const p of r.photo_highlights?.tables ?? []) {
      const start = p.stretch_start ?? p.at ?? null;
      if (!start) continue;
      const key = `${r.period_day}|${p.table_id ?? p.table_name}|${p.stretch_start ?? p.episode}`;
      const startMs = new Date(start).getTime();
      const minutes = p.cleared_after_min ?? 0;
      const endMs = p.cleared_at ? new Date(p.cleared_at).getTime() : startMs + minutes * 60_000;
      const ep = map.get(key) ?? { day: r.period_day, table: p.table_name ?? p.caption, startMs, endMs, minutes, photos: [], reportId: r.id, tableId: p.table_id, stretchStart: p.stretch_start };
      ep.photos.push(p);
      map.set(key, ep);
    }
  }
  return [...map.values()].sort((a, b) => b.minutes - a.minutes);
}

/** Сколько минут в кафе одновременно стояло 1 / 2 / 3+ неубранных стола. */
function concurrencyMinutes(eps: TableEpisode[]): { one: number; two: number; threePlus: number } {
  const out = { one: 0, two: 0, threePlus: 0 };
  const byDay = new Map<string, TableEpisode[]>();
  for (const e of eps) byDay.set(e.day, [...(byDay.get(e.day) ?? []), e]);
  for (const list of byDay.values()) {
    const events: [number, number][] = [];
    for (const e of list) { events.push([e.startMs, 1]); events.push([e.endMs, -1]); }
    events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    let n = 0; let t = events[0]?.[0] ?? 0;
    for (const [ts, d] of events) {
      const min = (ts - t) / 60_000;
      if (min > 0) { if (n === 1) out.one += min; else if (n === 2) out.two += min; else if (n >= 3) out.threePlus += min; }
      n += d; t = ts;
    }
  }
  return { one: Math.round(out.one), two: Math.round(out.two), threePlus: Math.round(out.threePlus) };
}

// ── Модель панели ─────────────────────────────────────────────────────────────

type Example = { day: string; photo: FindingsPhoto | DailyReportPhoto | null; caption: string };

/** Динамика против предыдущего периода. `rel` — относительная величина изменения
 *  (0.2 = на 20 %): ухудшение красится соразмерно, а не сразу красным (#1438 п.3). */
/** `value` — текущее число (с единицей), `change` — на сколько сдвинулось («+12 мин»), `what` —
 *  одно-два слова, что за число («очереди в день»). Число со стрелкой — главное в свёрнутой
 *  панели и в таблице филиалов (Алексей 23.10, #2065); `text` — подробно, в подсказке. */
export type Delta = { text: string; better: boolean | null; rel: number; /** Фактическое направление (рост/падение) — стрелка; цвет — по better. */ up?: boolean; value?: string; change?: string; what?: string;
  /** Что это за число — подсказка при наведении на цифру справа (Алексей 28.10 #3059). */ hint?: string };
function mkDelta(prev: number, cur: number, text: string, opts: { lowerIsBetter?: boolean; eps?: number; fmt?: (n: number) => string; what?: string; hint?: string } = {}): Delta {
  const { lowerIsBetter = true, eps = 0, fmt = (n: number) => String(Math.round(n)), what, hint } = opts;
  const diff = cur - prev;
  const rel = Math.abs(diff) / Math.max(Math.abs(prev), Math.abs(cur), 1e-9);
  const better = Math.abs(diff) <= eps ? null : lowerIsBetter ? diff < 0 : diff > 0;
  const change = better == null ? 'без изменений' : `${diff > 0 ? '+' : '−'}${fmt(Math.abs(diff))}`;
  return { text, better, rel: better == null ? 0 : rel, up: diff > 0, value: fmt(cur), change, what, hint };
}
const fmtPct = (n: number) => `${Math.round(n * 100)} %`;
const fmtMin = (n: number) => `${Math.round(n)} мин`;

/** Конкретное замечание: что не так, почему это замечание (под (i)), куда смотреть. */
export type Remark = { light: Light; text: string; why?: string; to?: string };

/** Выкладка: ряд = стеллаж/категория внутри критерия, раскрывается в фото этой проблемы. */
/** `episodes` — просадки этой категории эпизодами (начало → середина → конец, Алексей 21.10);
 *  без них (режим «все филиалы») ряд показывает `photos`. */
export type LayoutRow = { name: string; light: Light; metric: string; photos: Example[]; episodes?: DipEpisode[]; episodesLoading?: boolean };
export type LayoutGroup = { key: string; title: string; hint: string; light: Light; rows: LayoutRow[]; effects?: { label: string; value: string; hint: string }[]; effectsLoading?: boolean };

export type TaskRow = { text: string; due: string | null; done: number; total: number; ids: number[]; light: Light };
export type SourceRow = { name: string; rating: number | null; start: number | null; added: number; count: number | null; low: boolean; light: Light; href?: string };
export type SpeechBar = { id: string; title: string; badLabel: string; n: number; share: number; light: Light; to: string };
export type SpeechKind = { value: string; label: string; count: number; subjects: [string, number][]; items: OverviewProblem[]; to: string };
export type VisitBrief = { id: number; day: string; author: string; total: number; groups: { name: string; pct: number | null; light: Light }[]; failed: string[]; loading: boolean };

export type Panel = {
  key: string;
  title: string;
  light: Light;
  /** Сводка в свёрнутой панели: конкретно и actionable; у зелёных — метрики по сути (#1438 п.7). */
  headline: string;
  delta: Delta | null;
  remarks: Remark[];
  /** «В статистике» — кнопка в шапке панели. */
  stats?: { to: string; label?: string };
  /** Настройка — вторая кнопка (часы работы → «Общие»). */
  settings?: { to: string; label: string };
  groups?: LayoutGroup[];
  examples: Example[];
  /** `href` — внешняя ссылка, `to` — страница внутри; рисуется маленькой иконкой в строке. */
  details: { label: string; value: string; href?: string; to?: string; hint?: string }[];
  /** Эпизоды столов — та же цепочка кадров, что в отчёте дня (#1380). */
  episodes?: { caption: string; photos: DailyReportPhoto[]; minutes: number; reportId?: number; tableId?: number; stretchStart?: string }[];
  /** Длинные очереди сериями кадров (Алексей 21.10) — вместо одиночных `examples`. */
  queueEpisodes?: { threshold: number; total: number; items: QueueEpisode[] };
  /** Итоговая строка внизу панели — следствие основной проблемы (Алексей 22.10, #1843/#1845). */
  callout?: { minutes: number; perDayMin: number | null; text: string; hint: string; to?: string };
  /** Серии кадров ещё грузятся: вместо одиночных фото — заглушка, чтобы фото не «мутировало» в серию (#1840). */
  seriesLoading?: boolean;
  histogram?: { label: string; minutes: number }[];
  tasks?: TaskRow[];
  sources?: SourceRow[];
  themes?: { category: string; label: string; count: number; examples: string[]; reviews: ReviewDto[]; to: string }[];
  /** Что хвалят — по тем же темам, под спойлером (Алексей 22.10, #1849). */
  praise?: NonNullable<Panel['themes']>;
  speech?: { bars: SpeechBar[]; kinds: SpeechKind[]; analyzed: number };
  visit?: VisitBrief;
  /** Размер смен: за баром × гости у кассы и «справляется ли команда» по остальным панелям (#1850). */
  shifts?: ShiftsBlock;
  /** Приход — тем же элементом, что в Статистике: дни пилюлями + фото выбранного дня (Алексей 22.10, #1862). */
  arrivalCard?: { from: string; to: string };
  /** Подпись периода панели; по умолчанию STATUS_DAYS дней. Отзывы и визиты — 90 дней. */
  period?: string;
  /** Полоса дней в свёрнутой строке (#2298): уровень каждого дня окна, как на статус-страницах сервисов. */
  days?: DayBar[];
  /** Главное число строки, когда сравнить не с чем (нет прошлого окна) — чтобы справа всегда была цифра (#2301). */
  value?: { value: string; what: string };
  /** Раскрытие — финансовый результат плитками (FinanceBlock), Алексей 26.10 #2510. */
  finance?: boolean;
  /** План заказа — чего не хватило гостям, с советами про неподключённые источники (#2972). */
  orderPlan?: OrderPlanDto;
  /** Текст ячейки таблицы филиалов, если он не равен числу шапки (Финансы: выручка в день · средний чек). */
  cell?: string;
  /** Подсказка ячейки таблицы вместо «Раздел: суть», когда в ячейке два числа (#2523). */
  cellTip?: string;
  /** Связи с другими данными (#3189): «в дни с невыполненным чек-листом — плохие отзывы о чистоте». */
  links?: DataLinkDto[];
};
export type DayBar = { day: string; light: Light; text: string };

/** Уровень каждого дня окна по снимку; закрытые/без данных — серые. */
function dayBars(rows: DayRow[], f: (r: DayRow) => { light: Light; text: string } | null): DayBar[] {
  return rows.map((r) => {
    const v = r.data && !r.data.closed ? f(r) : null;
    return { day: r.day, light: v?.light ?? 'grey', text: `${fmtDay(r.day)} · ${v?.text ?? (r.data?.closed ? 'закрыто' : 'нет данных')}` };
  });
}

// ── Выкладка ─────────────────────────────────────────────────────────────────
// Критерии (Алексей 12.10, #1438 п.3): уровень на открытии, просадки, остатки на
// закрытии. Пороги — те же, что у отчёта дня: на открытии проблема ниже 35 %
// (OPENING_PROBLEM_PCT), остатки «большие» от 70 %.
const OPENING_PROBLEM_PCT = 35;
const LEFTOVER_FILL_PCT = 70;

/** Светофор доли дней с проблемой: половина дней — проблема, четверть — небольшая, иначе недочёт. */
function shareLight(n: number, days: number): Light {
  if (!days || n === 0) return 'green';
  const s = n / days;
  return s >= 0.5 ? 'orange' : s >= 0.25 ? 'yellow' : 'lime';
}

type RackMoment = { day: string; rack: string; type: string; fill: number; photo: DailyReportPhoto };
function rackMoments(reports: DailyReportDto[], caption: string): RackMoment[] {
  const out: RackMoment[] = [];
  for (const r of reports) {
    for (const p of r.photo_highlights?.layout ?? []) {
      if (p.caption !== caption || p.fill_pct == null) continue;
      out.push({ day: r.period_day, rack: p.rack_name ?? p.rack_type ?? '', type: p.rack_type ?? p.rack_name ?? '', fill: p.fill_pct, photo: p });
    }
  }
  return out;
}

/** Следствия просадок строками под разделом «Просадки» (#2507): очередь в минутах и чеки, если есть кассы. */
function dipEffects(d?: LayoutDipsResponse): { label: string; value: string; hint: string }[] {
  if (!d) return [];
  const out: { label: string; value: string; hint: string }[] = [];
  const pm = d.queue_configured ? d.total_queue_person_minutes : null;
  const days = d.days.length;
  // Формулировки проверены хайку-тестом (#2520): кто что сделал, число в конце; без «позиции витрины».
  if (pm && days) out.push({ label: 'Пока не хватало товара, у кассы стояли гости', value: `≈ ${Math.round(pm / days).toLocaleString('ru-RU')} мин в день`, hint: DIP_QUEUE_HINT });
  for (const [type, r] of Object.entries(d.receipts?.by_type ?? {})) {
    if (!r.checks) continue;
    out.push({ label: `${type}: пока не хватало товара, взяли`, value: `${r.with_category} раз — обычно в эти часы ${Math.round(r.expected_with_category)}`,
      hint: 'Сколько чеков с этим товаром пробили, пока витрина была ниже нормы, и сколько их бывает в те же часы таких же дней (будни/выходные) за 4 недели.' });
  }
  return out;
}

// «Дополнительный ракурс» — для алертов и дашборда, в статус не идёт (Алексей 26.10 #2525); дни, посчитанные
// до правки бэкенда, фильтруем здесь.
const NOT_IN_STATS = 'Дополнительный ракурс';

export function aggLayout(cur: DayRow[], prev: DayRow[], reports: DailyReportDto[], prevReports: DailyReportDto[], dipEpisodes?: LayoutDipsResponse, dipsLoading = false): Panel | null {
  type Cat = { rackType: string; problemDays: Set<string>; days: number; redDays: number; hours: number };
  const build = (rows: DayRow[]) => {
    const cats = new Map<string, Cat>();
    let configured = false; let days = 0;
    for (const r of rows) {
      const L = r.data?.layout;
      if (!L || !L.configured || r.data?.closed) continue;
      configured = true; days += 1;
      for (const c of L.categories ?? []) {
        if (c.rack_type === NOT_IN_STATS) continue;
        const a = cats.get(c.rack_type) ?? { rackType: c.rack_type, problemDays: new Set<string>(), days: 0, redDays: 0, hours: 0 };
        a.days += 1;
        // Просадка — только день, когда категория реально была ниже нормы (часы > 0). Жёлтый от открытия
        // или остатков на закрытии — не просадка: у них свои строки (Алексей 28.10 #3053, Tower: «выкладка 50 %»).
        if (c.status !== 'green' && (c.problem_hours ?? 0) > 0) a.problemDays.add(r.day);
        if (c.status === 'red') a.redDays += 1;
        a.hours += c.problem_hours ?? 0;
        cats.set(c.rack_type, a);
      }
    }
    return { configured, days, cats: [...cats.values()], hours: [...cats.values()].reduce((s, a) => s + a.hours, 0) };
  };
  const c = build(cur); const p = build(prev);
  if (!c.configured && !p.configured) return null;   // функция не настроена — панели не видно вовсе (AI184)
  const byDayDesc = (a: Example, b: Example) => (a.day < b.day ? 1 : -1);

  // Просадки — по категориям из вердиктов дня; фото — «Самая большая просадка» в проблемные дни.
  const dipMoments = rackMoments(reports, 'Самая большая просадка');
  const dips: LayoutRow[] = c.cats.map((a) => {
    const perDay = a.days ? a.hours / a.days : 0;
    // 🔴 — только когда откровенно ужасно: отчёт дня красил категорию красным как минимум
    // половину дней. Иначе по парку всё красное и приоритета нет (24.09).
    const light: Light = a.days === 0 ? 'grey'
      : a.redDays >= Math.max(3, Math.ceil(a.days / 2)) ? 'red'
        // Оранжевый — когда категория плоха регулярно (четверть дней красная или 60 % с просадкой),
        // а не от двух красных дней из 14 (#2279: «всё красное у всех»).
        : a.redDays >= Math.max(2, Math.ceil(a.days / 4)) || perDay >= 4 || a.problemDays.size / a.days >= 0.6 ? 'orange'
          : a.problemDays.size / a.days >= 0.25 ? 'yellow'
            : a.problemDays.size > 0 ? 'lime' : 'green';
    const photos = dipMoments.filter((m) => m.type === a.rackType && a.problemDays.has(m.day))
      .map((m) => ({ day: m.day, photo: m.photo, caption: `${fmtDay(m.day)} · ${m.rack} · самая большая просадка${m.photo.at ? ` в ${hhmm(m.photo.at)}` : ''} · выкладка ${Math.round(m.fill)} %` }))
      .sort(byDayDesc);
    // Эпизоды — из той же детекции, что отчёт дня (layout-dips); самые долгие первыми.
    const episodes = dipEpisodes?.days.flatMap((d) => d.episodes).filter((ep) => (ep.rack_type ?? '') === a.rackType)
      .sort((x, y) => y.minutes - x.minutes);
    // Алексей 21.10 #7045: «4.2 ч · 1 из 14 дн. · 16 эпизодов — что значит?» — часы и дни были из вердиктов
    // отчёта (только дни с ≥ 3 ч, средневзвешенно по полкам), а эпизоды — все провалы ≥ 30 мин. Когда эпизоды
    // есть, сводка считается по ним же: сколько эпизодов, в скольких днях, суммарно.
    const epDays = new Set((episodes ?? []).map((ep) => fmtDay(ep.start.slice(0, 10)))).size;
    const epHours = (episodes ?? []).reduce((sum, ep) => sum + ep.minutes, 0) / 60;
    // #2333: «8 из 13 дн. · до 97 % · 8 фото — тяжело читать» — коротко, по-человечески: сколько и за сколько дней.
    const days = (n: number) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;
    const metric = episodes?.length
      ? `${fmtHours(epHours)} за ${days(epDays)}`
      : a.hours === 0 ? 'просадок не было' : `${fmtHours(a.hours)} за ${days(a.problemDays.size)}`;
    return { name: a.rackType || 'Без категории', light, metric, photos, episodes, episodesLoading: dipsLoading && !dipEpisodes };
  });

  // На открытии / остатки на закрытии — по стеллажам из подписанных моментов отчёта дня.
  const byRack = (moments: RackMoment[], bad: (fill: number) => boolean, worstFill: (fills: number[]) => number, label: (m: RackMoment) => string, summary: (n: number, days: number, worst: number) => string) => {
    const racks = new Map<string, { moments: RackMoment[]; bad: RackMoment[] }>();
    for (const m of moments) {
      const r = racks.get(m.rack) ?? { moments: [], bad: [] };
      r.moments.push(m); if (bad(m.fill)) r.bad.push(m);
      racks.set(m.rack, r);
    }
    const rows: LayoutRow[] = [];
    for (const [rack, r] of racks) {
      if (!r.bad.length) continue;
      rows.push({
        name: rack, light: shareLight(r.bad.length, r.moments.length),
        metric: summary(r.bad.length, r.moments.length, worstFill(r.bad.map((m) => m.fill))),
        photos: r.bad.map((m) => ({ day: m.day, photo: m.photo, caption: label(m) })).sort(byDayDesc),
      });
    }
    return rows.sort((a, b) => LIGHT_ORDER[a.light] - LIGHT_ORDER[b.light] || b.photos.length - a.photos.length);
  };
  const labelOpening = (m: RackMoment) => `${fmtDay(m.day)} · ${m.rack} · на открытии${m.photo.at ? ` в ${hhmm(m.photo.at)}` : ''} выложено ${Math.round(m.fill)} %`;
  const labelClosing = (m: RackMoment) => `${fmtDay(m.day)} · ${m.rack} · перед закрытием${m.photo.at ? ` в ${hhmm(m.photo.at)}` : ''} осталось ${Math.round(m.fill)} %`;
  const opening = byRack(rackMoments(reports, 'На открытии'), (f) => f < OPENING_PROBLEM_PCT, (fs) => Math.min(...fs), labelOpening, (n, _d, w) => `${n} ${plural(n, 'день', 'дня', 'дней')}, минимум ${Math.round(w)} %`);
  const closing = byRack(rackMoments(reports, 'Перед закрытием'), (f) => f >= LEFTOVER_FILL_PCT, (fs) => Math.max(...fs), labelClosing, (n, _d, w) => `${n} ${plural(n, 'вечер', 'вечера', 'вечеров')}, в худший осталось ${Math.round(w)} % товара`);
  const prevOpening = rackMoments(prevReports, 'На открытии').filter((m) => m.fill < OPENING_PROBLEM_PCT).length;
  const curOpening = opening.reduce((s, r) => s + r.photos.length, 0);
  const prevClosing = rackMoments(prevReports, 'Перед закрытием').filter((m) => m.fill >= LEFTOVER_FILL_PCT).length;
  const curClosing = closing.reduce((s, r) => s + r.photos.length, 0);

  const dipRows = dips.filter((r) => r.light !== 'green' && r.light !== 'grey').sort((a, b) => LIGHT_ORDER[a.light] - LIGHT_ORDER[b.light]);
  const groups: LayoutGroup[] = [
    { key: 'opening', title: 'Уровень на открытии', hint: `Проблема, если в первые полчаса выложено меньше ${OPENING_PROBLEM_PCT} %`, light: worstOf(opening.map((r) => r.light)), rows: opening },
    { key: 'dips', title: 'Просадки', hint: 'Часы, когда выкладка держалась ниже нормы стеллажа', light: worstOf(dipRows.map((r) => r.light), c.cats.length ? 'green' : 'grey'), rows: dipRows, effects: dipEffects(dipEpisodes), effectsLoading: dipsLoading },
    { key: 'closing', title: 'Остатки на закрытии', hint: `Остаток от ${LEFTOVER_FILL_PCT} % за 20 минут до закрытия — выложили больше, чем продали`, light: worstOf(closing.map((r) => r.light)), rows: closing },
  ];
  const light: Light = !c.configured ? 'grey' : worstOf(groups.map((g) => g.light));
  const remarks: Remark[] = [];
  if (dipRows.length) remarks.push({ light: groups[1].light, text: `Просадки ${fmtHours(c.hours)} за ${c.days} дн.: ${dipRows.map((r) => r.name).join(', ')}`, why: groups[1].hint, to: STATS.layout });
  if (opening.length) remarks.push({ light: groups[0].light, text: `На открытии ниже ${OPENING_PROBLEM_PCT} %: ${opening.map((r) => `${r.name} ${r.photos.length} из ${rackMoments(reports, 'На открытии').filter((m) => m.rack === r.name).length} дн.`).join(', ')}`, why: groups[0].hint, to: STATS.layout });
  if (closing.length) remarks.push({ light: groups[2].light, text: `Остатки на закрытии: ${closing.map((r) => `${r.name} ${r.photos.length} ${plural(r.photos.length, 'вечер', 'вечера', 'вечеров')}`).join(', ')}`, why: groups[2].hint, to: STATS.layout });
  // Люди в очереди, пока выкладка проседала, — усугубляет просадку, поэтому замечанием,
  // а не строкой в деталях (Алексей 22.10, #1840). Нет зоны очереди — строки нет (не ноль).
  const dipPm = dipEpisodes?.queue_configured ? dipEpisodes.total_queue_person_minutes : null;
  // TODO(Алексей 22.10, #1848; напоминание хелперу на 12.11): когда подключим чековые данные
  // от Жени (SurfCoffee), заменить эту строку на «За время просадок было сделано X заказов
  // без еды и Y с низкомаржинальными позициями еды». Люди в прикассовой зоне — временный
  // заменитель: следствие просадки в деньгах покажут только чеки.
  // Делим на открытые дни, за которые посчитаны просадки (закрытые дни BE не отдаёт).
  const dipDays = dipEpisodes?.days.length ?? 0;
  // Большой блок «N мин посетители провели…» убран внутрь раздела «Просадки» компактной строкой (#2507).
  const callout = undefined;
  void dipDays;
  const okParts = [c.hours === 0 ? 'просадок не было' : `просадки ${fmtHours(c.hours)} за ${c.days} дн.`, opening.length ? '' : 'на открытии в норме', closing.length ? '' : 'остатки на закрытии в норме'].filter(Boolean);
  return {
    key: 'layout', title: 'Выкладка', light,
    days: dayBars(cur, (r) => {
      const L = r.data?.layout; if (!L?.configured) return null;
      const cats = (L.categories ?? []).filter((x) => x.rack_type !== NOT_IN_STATS);
      const red = cats.filter((x) => x.status === 'red').length; const yel = cats.filter((x) => x.status === 'yellow').length;
      const h = cats.reduce((sum, x) => sum + (x.problem_hours ?? 0), 0);
      return { light: red ? 'red' : yel ? 'yellow' : 'green', text: red || yel ? `просадки ${fmtHours(h)}${red ? ` · красных категорий ${red}` : ''}` : 'в норме' };
    }),
    headline: !c.configured ? 'Выкладка не настроена' : remarks.length ? remarks.map((r) => r.text).join(' · ') : okParts.join(' · '),
    delta: p.configured && (c.hours || p.hours) ? mkDelta(p.hours, c.hours, `${p.hours ? fmtHours(p.hours) : '0'} → ${fmtHours(c.hours)} просадок`, { eps: 1, fmt: fmtHours, what: 'просадок' })
      : p.configured && (curOpening || prevOpening || curClosing || prevClosing) ? mkDelta(prevOpening + prevClosing, curOpening + curClosing, `${prevOpening + prevClosing} → ${curOpening + curClosing} дн. с замечаниями по открытию и закрытию`, { fmt: (n) => `${Math.round(n)} дн.`, what: 'с замечаниями' }) : null,
    remarks, stats: { to: STATS.layout }, callout,
    groups, examples: [],
    // «В целом как параметр в секции выкладка человеко-минут посетителей за время этих
    // просадок» (Алексей 21.10). Нет зоны очереди — строки нет (не ноль).
    details: dipPm != null && !dipRows.length ? [{
      label: `Посетителей в очереди за время просадок, за ${STATUS_DAYS} дн.`,
      value: `${Math.round(dipPm).toLocaleString('ru-RU')} мин`,
      hint: DIP_QUEUE_HINT,
    }] : [],
  };
}

// ── Очередь ──────────────────────────────────────────────────────────────────

/** Сколько эпизодов очереди просить у BE: «Статусу» нужны главные, не сотни за две недели. */
export const QUEUE_EPISODES_TOP = 12;

/** `episodes` — эпизоды «очередь от 4 человек» сериями кадров (queue-episodes); без них
 *  (режим «все филиалы», BE не ответил) панель показывает одиночные фото пика дня. */
export function aggQueue(cur: DayRow[], prev: DayRow[], episodes?: QueueEpisodesResponse, episodesLoading = false): Panel | null {
  const build = (rows: DayRow[]) => {
    let configured = false; let days = 0; let peakSum = 0; let peakMax = 0; let peakDay = ''; let longEvents = 0; let longMin = 0; let longDays = 0;
    let secondConfigured = false; let secondMin = 0; const decrease: number[] = [];
    const examples: (Example & { minutes: number })[] = [];
    for (const r of rows) {
      const Q = r.data?.queue;
      if (!Q || !Q.configured || r.data?.closed) continue;
      configured = true; days += 1;
      const pk = Q.peak_people ?? 0;
      peakSum += pk; if (pk > peakMax) { peakMax = pk; peakDay = r.day; }
      const lm = Q.long_queue_minutes ?? 0;
      longEvents += Q.long_queue_events ?? 0; longMin += lm; if (lm > 0) longDays += 1;
      // Вторая касса считается только там, где касс две (BE отдаёт null, если одна) — #1438 доп.
      if (Q.second_cashier_minutes != null) { secondConfigured = true; secondMin += Q.second_cashier_minutes; }
      if (Q.decrease_min != null) decrease.push(Q.decrease_min);
      if ((lm > 0 || (Q.long_queue_events ?? 0) > 0) && Q.photo) {
        const at = Q.peak_at ? hhmm(Q.peak_at) : '';
        examples.push({ day: r.day, photo: Q.photo, minutes: lm, caption: `${fmtDay(r.day)} · пик ${pk} чел.${at ? ` в ${at}` : ''}${lm ? ` · очередь 4+ чел. ${Math.round(lm)} мин за день` : ''}${Q.decrease_min != null ? ` · спала за ${Math.round(Q.decrease_min)} мин` : ''}` });
      }
    }
    examples.sort((a, b) => b.minutes - a.minutes);
    return { configured, days, avgPeak: days ? peakSum / days : 0, peakMax, peakDay, longEvents, longMin, longDays, secondConfigured, secondMin,
      decrease: decrease.length ? decrease.reduce((a, b) => a + b, 0) / decrease.length : null, examples: examples.slice(0, 8) };
  };
  const c = build(cur); const p = build(prev);
  if (!c.configured && !p.configured) return null;
  // Светофор — по минутам очереди от 4 человек в день, как в отчёте дня (30 → небольшая, 60 → проблема), заметная от двух часов.
  const minPerDay = c.days ? c.longMin / c.days : 0;
  const queueLight: Light = !c.configured || c.days === 0 ? 'grey' : minPerDay >= 120 ? 'red' : minPerDay >= 60 ? 'orange' : minPerDay >= 30 ? 'yellow' : minPerDay >= 10 ? 'lime' : 'green';
  const remarks: Remark[] = [];
  if (isProblem(queueLight)) remarks.push({ light: queueLight, text: `Очередь от 4 человек — ${Math.round(minPerDay)} мин в день, ${c.longDays} из ${c.days} дн. (всего ${fmtHours(c.longMin / 60)})`, why: 'Минуты, когда у кассы стояло четверо и больше: порог отчёта дня — 30 мин в день', to: STATS.queue });
  if (c.peakMax >= 8) remarks.push({ light: 'yellow', text: `Пик до ${c.peakMax} чел.${c.peakDay ? ` ${fmtDay(c.peakDay)}` : ''}`, why: 'Очередь от 8 человек — гости уходят, не дождавшись', to: STATS.queue });
  if (c.secondConfigured && c.longMin >= 30 && c.secondMin < c.longMin * 0.3) remarks.push({ light: 'lime', text: `Очередь от 4 человек стояла ${fmtHours(c.longMin / 60)} за ${c.days} дн., вторая касса — ${fmtHours(c.secondMin / 60)} из них`, why: 'Доля времени очереди от 4 человек, когда работала вторая касса', to: STATS.queue });
  const light = worstOf(remarks.map((r) => r.light), queueLight === 'grey' ? 'grey' : 'green');
  const prevMinPerDay = p.days ? p.longMin / p.days : null;
  // С кадрами — первыми, затем самые долгие, при равенстве — где очередь длиннее.
  const queueEpisodes = episodes?.days.flatMap((d) => d.episodes)
    .sort((x, y) => Number(y.photos.length > 0) - Number(x.photos.length > 0) || y.minutes - x.minutes || y.peak_people - x.peak_people);
  const seriesShown = !!queueEpisodes?.some((ep) => ep.photos.length > 0);
  return {
    key: 'queue', title: 'Очередь на кассе', light,
    days: dayBars(cur, (r) => {
      const Q = r.data?.queue; if (!Q?.configured) return null;
      const lm = Q.long_queue_minutes ?? 0;
      return { light: lm >= 120 ? 'red' : lm >= 60 ? 'orange' : lm >= 30 ? 'yellow' : lm >= 10 ? 'lime' : 'green', text: lm ? `очередь 4+ чел. ${Math.round(lm)} мин` : 'без очередей' };
    }),
    headline: !c.configured ? 'Очередь не настроена' : remarks.length ? remarks.map((r) => r.text).join(' · ')
      : `Очередь от 4 человек ${c.longMin === 0 && c.longEvents === 0 ? 'не стояла' : `— ${Math.round(minPerDay)} мин в день`} · в пике обычно ${Math.round(c.avgPeak)} чел.${c.decrease != null ? ` · уходит за ${Math.round(c.decrease)} мин` : ''}`,
    delta: prevMinPerDay != null ? mkDelta(prevMinPerDay, minPerDay, `${Math.round(prevMinPerDay)} → ${Math.round(minPerDay)} мин очереди в день`, { eps: 5, fmt: fmtMin, what: 'в день', hint: 'Сколько минут в день у кассы стояло 4 человека и больше' }) : null,
    remarks, stats: { to: STATS.queue },
    examples: seriesShown || (episodesLoading && !episodes) ? [] : c.examples,
    seriesLoading: episodesLoading && !episodes,
    queueEpisodes: seriesShown && queueEpisodes ? { threshold: episodes?.threshold ?? 4, total: episodes?.episodes_total ?? queueEpisodes.length, items: queueEpisodes } : undefined,
    details: [
      { label: 'Пик за день, в среднем', value: `${c.avgPeak.toFixed(1)} чел.` },
      { label: 'Очередь уходит за', value: c.decrease != null ? `${Math.round(c.decrease)} мин` : '—' },
      ...(c.secondConfigured ? [{ label: 'Вторая касса во время очередей', value: c.secondMin ? fmtHours(c.secondMin / 60) : 'не открывалась' }] : []),
    ],
  };
}

// ── Столы ────────────────────────────────────────────────────────────────────

export function aggTables(cur: DayRow[], prev: DayRow[], reports: DailyReportDto[], prevReports: DailyReportDto[]): Panel | null {
  const configuredDays = (rows: DayRow[]) => rows.filter((r) => r.data?.tables && !r.data.closed && r.data.tables.total_tables).length;
  const cDays = configuredDays(cur); const pDays = configuredDays(prev);
  if (cDays === 0 && pDays === 0) return null;
  const build = (reps: DailyReportDto[]) => {
    const eps = tableEpisodes(reps);
    const over5 = eps.filter((e) => e.minutes > 5); const over15 = eps.filter((e) => e.minutes > 15);
    const total = eps.reduce((sum, e) => sum + e.minutes, 0);
    const days = new Set(reps.map((r) => r.period_day)).size;
    return { eps, over5, over15, total, days, conc: concurrencyMinutes(eps) };
  };
  // Без отчётов дня (режим «все филиалы») — считаем по гистограмме уборки из вердиктов.
  const fromFindings = (rows: DayRow[]) => {
    let over5 = 0; let over15 = 0; let days = 0;
    for (const r of rows) {
      const h = r.data?.tables?.cleanup_histogram;
      if (!h || r.data?.closed) continue;
      days += 1; over15 += h.gt_15m; over5 += h['5_15m'] + h.gt_15m;
    }
    return { eps: [] as TableEpisode[], over5: Array<TableEpisode>(over5), over15: Array<TableEpisode>(over15), total: 0, days, conc: { one: 0, two: 0, threePlus: 0 } };
  };
  const c = reports.length ? build(reports) : fromFindings(cur);
  const p = prevReports.length ? build(prevReports) : fromFindings(prev);
  const noMinutes = !reports.length;
  const days = c.days || cDays;
  const over15PerDay = days ? c.over15.length / days : 0;
  // Цвет — на размер зала (Алексей 24.10 #2279: «всё красное у всех»): 5 долгих уборок в день на
  // 21 стол — не то же, что 5 на 6 столов. Пороги прежние, но на каждые 8 столов.
  const tablesN = Math.max(...cur.map((r) => (!r.data?.closed && r.data?.tables?.total_tables) || 0), 0);
  const perSize = over15PerDay / Math.max(1, tablesN / 8);
  const light: Light = cDays === 0 ? 'grey' : perSize >= 6 ? 'red' : perSize >= 3 ? 'orange' : perSize >= 1 ? 'yellow' : c.over15.length > 0 ? 'lime' : 'green';
  const remarks: Remark[] = [];
  if (isProblem(light)) remarks.push({ light, text: `Столы грязными дольше 15 мин — ${c.over15.length} ${plural(c.over15.length, 'раз', 'раза', 'раз')} за ${days} дн.${c.eps[0] ? ` · дольше всех ${c.eps[0].table} ${c.eps[0].minutes} мин ${fmtDay(c.eps[0].day)}` : ''}`, why: 'Эпизод = стол грязный от сигнала до первого чистого кадра; 15 минут — порог отчёта дня', to: STATS.tables });
  const twoPlus = (c.conc.two + c.conc.threePlus) / 60;
  if (!noMinutes && twoPlus >= 1) remarks.push({ light: twoPlus >= 3 ? 'yellow' : 'lime', text: `2+ грязных стола одновременно — ${fmtHours(twoPlus)}`, why: 'Когда грязных столов несколько сразу, гостям негде сесть', to: STATS.tables });
  const episodes = c.over5.slice(0, 6).map((e) => ({
    caption: `${fmtDay(e.day)} · ${e.table} · ${hhmm(new Date(e.startMs).toISOString())}–${hhmm(new Date(e.endMs).toISOString())}`,
    minutes: e.minutes,
    reportId: e.reportId, tableId: e.tableId, stretchStart: e.stretchStart,
    photos: e.photos.filter((ph) => ph.image_url),
  })).filter((e) => e.photos.length > 0);
  const prevTotalPerDay = p.days ? p.total / p.days : null; const totalPerDay = days ? c.total / days : 0;
  return {
    key: 'tables', title: 'Столы', light: worstOf(remarks.map((r) => r.light), light === 'grey' ? 'grey' : 'green'),
    days: dayBars(cur, (r) => {
      const h = r.data?.tables?.cleanup_histogram; if (!h) return null;
      const n = h.gt_15m / Math.max(1, (r.data?.tables?.total_tables ?? 0) / 8);
      return { light: n >= 6 ? 'red' : n >= 3 ? 'orange' : n >= 1 ? 'yellow' : h.gt_15m > 0 ? 'lime' : 'green', text: h.gt_15m ? `грязные дольше 15 мин: ${h.gt_15m}` : 'убирали вовремя' };
    }),
    headline: cDays === 0 ? 'Нет данных по столам'
      : remarks.length ? remarks.map((r) => r.text).join(' · ')
        : noMinutes ? `Столов дольше 15 мин не бросали · дольше 5 мин — ${c.over5.length} за ${days} дн.`
          : c.eps.length === 0 ? 'Грязных столов в отчётах не было'
            : `Неубранных столов ${fmtHours(c.total / 60)} за ${days} дн. · дольше 5 мин — ${c.over5.length}, дольше 15 — ${c.over15.length}`,
    delta: noMinutes
      ? (p.days ? mkDelta(p.over15.length, c.over15.length, `${p.over15.length} → ${c.over15.length} дольше 15 мин`, { what: 'дольше 15 мин' }) : null)
      : prevTotalPerDay != null ? mkDelta(prevTotalPerDay, totalPerDay, `${Math.round(prevTotalPerDay)} → ${Math.round(totalPerDay)} мин неубранных столов в день`, { eps: 5, fmt: fmtMin, what: 'в день', hint: 'Сколько минут в день столы стояли грязными: по каждому столу от сигнала «грязный» до первого чистого кадра, по всем столам вместе (два грязных стола 10 мин = 20 мин)' }) : null,
    remarks, stats: { to: STATS.tables },
    examples: [], episodes,
    details: [],
    histogram: noMinutes ? undefined : [
      { label: '1 неубранный стол', minutes: c.conc.one },
      { label: '2 одновременно', minutes: c.conc.two },
      { label: '3 и больше', minutes: c.conc.threePlus },
    ],
  };
}

// ── Чек-листы ────────────────────────────────────────────────────────────────

export function aggTasks(cur: DayRow[], prev: DayRow[], enabled: boolean): Panel | null {
  if (!enabled) return null;   // чек-листы выключены в Общих настройках — не показываем (AI184)
  const build = (rows: DayRow[]) => {
    let days = 0; let total = 0; let done = 0;
    const chronic = new Map<string, { text: string; due: string | null; total: number; done: number; ids: Set<number> }>();
    for (const r of rows) {
      const K = r.data?.tasks;
      if (!K || r.data?.closed || !K.total) continue;
      days += 1; total += K.total; done += K.done;
      for (const cf of K.chronic_fail ?? []) {
        const key = `${cf.task_text}|${cf.due_time ?? ''}`;
        const a = chronic.get(key) ?? { text: cf.task_text, due: cf.due_time, total: 0, done: 0, ids: new Set<number>() };
        a.total += cf.total; a.done += cf.done;
        for (const id of cf.checklist_item_ids ?? [cf.checklist_item_id]) a.ids.add(id);
        chronic.set(key, a);
      }
    }
    const rate = total ? done / total : null;
    const taskRows: TaskRow[] = [...chronic.values()].filter((x) => x.total - x.done > 0)
      .map((x): TaskRow => ({ text: x.text, due: x.due ? x.due.slice(0, 5) : null, done: x.done, total: x.total, ids: [...x.ids], light: x.done === 0 ? 'orange' : x.done / x.total < 0.5 ? 'yellow' : 'lime' }))
      .sort((x, y) => (y.total - y.done) - (x.total - x.done));
    return { days, total, done, rate, rows: taskRows };
  };
  const c = build(cur); const p = build(prev);
  const notDone = c.rate == null ? null : 1 - c.rate;
  const light: Light = c.days === 0 || notDone == null ? 'grey' : notDone > 0.5 ? 'red' : notDone > 0.25 ? 'orange' : notDone > 0.1 ? 'yellow' : notDone > 0 ? 'lime' : 'green';
  const remarks: Remark[] = [];
  if (isProblem(light)) {
    remarks.push({ light, text: `Выполнено ${c.done} из ${c.total} задач (${Math.round((c.rate ?? 0) * 100)} %) за ${c.days} дн.`, why: 'Задача считается выполненной, когда по ней пришёл отчёт в чат точки', to: STATS.tasks });
    // Список невыполняемых задач — только внизу панели («Не выполняются», `tasks`), в выводах не дублируем
    // (Алексей 21.10 #7023: «надо это только внизу писать в списке Не выполняются, этого достаточно»).
  }
  return {
    key: 'tasks', title: 'Чек-листы', light,
    days: dayBars(cur, (r) => {
      const K = r.data?.tasks; if (!K?.total) return null;
      const nd = 1 - K.done / K.total;
      return { light: nd > 0.5 ? 'red' : nd > 0.25 ? 'orange' : nd > 0.1 ? 'yellow' : nd > 0 ? 'lime' : 'green', text: `выполнено ${K.done} из ${K.total}` };
    }),
    headline: c.days === 0 ? 'Чек-листы не настроены или нет данных'
      : remarks.length ? remarks.map((r) => r.text).join(' · ') : `Выполнено ${c.done} из ${c.total} задач за ${c.days} дн. · всё по плану`,
    delta: p.rate != null && c.rate != null ? mkDelta(p.rate, c.rate, `${Math.round(p.rate * 100)} % → ${Math.round(c.rate * 100)} % выполнено`, { lowerIsBetter: false, eps: 0.02, fmt: fmtPct, what: 'выполнено' }) : null,
    // Процент — и без прошлого окна (Ратуша: был кружок без числа, Алексей 04.11 #4054): нет динамики — нет стрелки, а не числа.
    value: c.rate != null ? { value: fmtPct(c.rate), what: 'выполнено' } : undefined,
    remarks, stats: { to: STATS.tasks }, settings: { to: '/management/checklists', label: 'Чек-листы' },
    examples: [], details: [],
    tasks: c.rows,
  };
}

// ── Речь ─────────────────────────────────────────────────────────────────────

/** Речь — из того же источника, что вкладка «Речь» в Статистике (conversations/overview:
 *  бары пресета с подписями и тонами). Старый summary считал по quality_score, который
 *  новый пайплайн не пишет, — отсюда «апселл 0 %» (владелец 09.10, AI238). */
const BAR_BAD_PHRASE: Record<string, string> = { upsell: 'без апселла', communication: 'сухое общение', loyalty: 'лицензию не назвали' };
const BAR_WHY: Record<string, string> = {
  upsell: 'Разговоры, где кассир ничего не предложил к заказу',
  communication: 'Разговоры без живого общения — только приём заказа',
  loyalty: 'Разговоры, где программу лояльности не назвали ни «лицензией», ни «лицензией серфера»',
};
const SEV_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function barBad(b: OverviewBar): { n: number; share: number; label: string } {
  const bad = b.values.filter((v) => v.tone === 'bad');
  const n = bad.reduce((sum, v) => sum + v.count, 0);
  const denom = Math.max(1, b.total - b.no_data);
  return { n, share: n / denom, label: bad.map((v) => v.label).join(', ') };
}
const shareLightSpeech = (s: number): Light => (s >= 0.75 ? 'red' : s >= 0.55 ? 'orange' : s >= 0.35 ? 'yellow' : s >= 0.2 ? 'lime' : 'green');

export function aggSpeech(cur: ConversationsOverview | null | undefined, prev: ConversationsOverview | null | undefined): Panel | null {
  if (!cur) return null;
  if (!cur.analyzed && !prev?.analyzed) {
    return { key: 'speech', title: 'Общение с гостями', light: 'grey', headline: 'Разговоров за период нет', delta: null, remarks: [], examples: [], details: [], stats: { to: STATS.speech } };
  }
  const bars: SpeechBar[] = cur.bars.map((b) => {
    const bb = barBad(b);
    const badLabel = BAR_BAD_PHRASE[b.id] ?? bb.label;
    return { id: b.id, title: b.title, badLabel, n: bb.n, share: bb.share, light: shareLightSpeech(bb.share), to: `${STATS.speech}&q=${encodeURIComponent(`${b.title.toLowerCase()}: ${bb.label}`)}` };
  });
  const prevBars = new Map((prev?.bars ?? []).map((b) => [b.id, barBad(b)]));
  const problems = cur.problems?.total ?? 0;
  const problemShare = cur.analyzed ? problems / cur.analyzed : 0;
  const problemLight: Light = problemShare >= 0.3 ? 'orange' : problemShare >= 0.15 ? 'yellow' : problemShare >= 0.05 ? 'lime' : 'green';
  const items = cur.problems?.items ?? [];
  const kinds: SpeechKind[] = [...(cur.problems?.kinds ?? [])].sort((a, b) => b.count - a.count).map((k) => ({
    value: k.value, label: k.label, count: k.count, subjects: k.subjects.slice(0, 5),
    items: items.filter((it) => (it.kind ?? it.category) === k.value).sort((a, b) => (SEV_ORDER[a.severity ?? 'low'] ?? 2) - (SEV_ORDER[b.severity ?? 'low'] ?? 2)),
    to: `${STATS.speech}&q=${encodeURIComponent(`проблема: ${k.label}`)}`,
  }));
  const remarks: Remark[] = [];
  for (const b of bars) {
    if (isProblem(b.light)) remarks.push({ light: b.light, text: `${b.badLabel[0].toUpperCase()}${b.badLabel.slice(1)} — ${Math.round(b.share * 100)} % (${b.n} из ${cur.analyzed})`, why: BAR_WHY[b.id], to: b.to });
  }
  if (isProblem(problemLight) || problems > 0) {
    const top = kinds.slice(0, 2).map((k) => `${k.label} ${k.count}${k.subjects.length ? ` (${k.subjects.slice(0, 2).map(([s]) => s).join(', ')})` : ''}`).join(', ');
    remarks.push({ light: problemLight === 'green' ? 'lime' : problemLight, text: `Проблемы в ${problems} ${plural(problems, 'разговоре', 'разговорах', 'разговорах')}: ${top}`, why: 'Гость просил и не получил, жаловался или что-то пошло не так — по разбору разговора', to: STATS.speech });
  }
  const light: Light = !cur.analyzed ? 'grey' : worstOf(remarks.map((r) => r.light));
  const upsell = bars.find((b) => b.id === 'upsell'); const pu = prevBars.get('upsell');
  return {
    key: 'speech', title: 'Общение с гостями', light,
    headline: remarks.length ? `${remarks.map((r) => r.text).join(' · ')}` : `${cur.analyzed} ${plural(cur.analyzed, 'разговор', 'разговора', 'разговоров')} · ${bars.map((b) => `${b.badLabel} ${Math.round(b.share * 100)} %`).join(' · ')}`,
    // Число в ячейке «все филиалы» есть всегда, и без прошлого периода (Алексей 28.10 #3179: «ярд в речи
    // жёлтая точка, а не цифра») — доля без апселла, а без бара апселла — доля разговоров с проблемой.
    cell: cur.analyzed ? (upsell ? fmtPct(upsell.share) : fmtPct(problemShare)) : undefined,
    delta: upsell && pu && prev?.analyzed ? mkDelta(pu.share, upsell.share, `без апселла ${Math.round(pu.share * 100)} % → ${Math.round(upsell.share * 100)} %`, { eps: 0.01, fmt: fmtPct, what: 'без апселла' }) : null,
    remarks, stats: { to: STATS.speech },
    examples: [], details: [],
    speech: { bars, kinds, analyzed: cur.analyzed },
  };
}

// ── Отзывы ───────────────────────────────────────────────────────────────────

const SRC_NAME: Record<string, string> = { brand_site: 'Сайт', yandex: 'Яндекс Карты', '2gis': '2ГИС' };
const LOW_REVIEWS = Math.round(RARE_DAYS / 7);   // меньше отзыва в неделю

export function aggReviews(cur: ReviewsStatsDto | null | undefined, prev: ReviewsStatsDto | null | undefined): Panel | null {
  if (!cur) return null;
  const p = cur.period; const pp = prev?.period;
  if (!p.count && !pp?.count) {
    return { key: 'reviews', title: 'Отзывы', light: 'grey', headline: `Отзывов за ${RARE_DAYS} дней нет`, delta: null, remarks: [], examples: [], details: [], period: `${RARE_DAYS} дней`, stats: { to: STATS.reviews } };
  }
  const neg = Object.entries(p.by_rating).reduce((sum, [r, n]) => sum + (Number(r) <= 3 ? n : 0), 0);
  const avg = p.avg_rating;
  const remarks: Remark[] = [];
  // Оценка на картах по источникам (#1382): <4.8 небольшая, <4.5 проблема, <4 заметная; QR сюда не входит.
  const sources: SourceRow[] = (cur.sources ?? []).filter((src) => src.provider === 'yandex' || src.provider === '2gis').map((src) => {
    const r = src.rating;
    const sl: Light = r == null ? 'grey' : r < 4 ? 'red' : r < 4.5 ? 'orange' : r < 4.8 ? 'yellow' : 'green';
    const added = src.new_in_period ?? src.review_count_delta ?? 0;
    const count = src.rating_count ?? src.review_count ?? null;
    // Низкий прирост — замечание только при малом числе отзывов ИЛИ неидеальной оценке (#1438 п.5).
    const low = added < LOW_REVIEWS && ((count != null && count < 50) || (r != null && r < 4.9));
    return { name: SRC_NAME[src.provider] ?? src.provider, rating: r, start: src.rating_at_start ?? null, added, count, low, light: sl, href: src.public_url || src.url || undefined };
  });
  for (const s of sources) {
    if (s.rating != null && isProblem(s.light)) remarks.push({ light: s.light, text: `${s.name}: оценка ${s.rating.toFixed(1)}${s.start != null && Math.abs(s.start - s.rating) >= 0.05 ? ` (было ${s.start.toFixed(1)})` : ''}`, why: 'Оценку на карте видят все гости до визита; ниже 4.5 заметно режет поток', to: STATS.reviews });
  }
  const lowSrc = sources.filter((s) => s.low);
  if (p.count < LOW_REVIEWS) remarks.push({ light: 'yellow', text: `Мало отзывов: ${p.count} за ${RARE_DAYS} дней`, why: `Меньше одного в неделю — оценка на картах не растёт, а один негативный отзыв сильно её роняет`, to: STATS.reviews });
  if (lowSrc.length) remarks.push({ light: 'lime', text: `Низкий прирост на ${lowSrc.map((s) => `${s.name} (+${s.added})`).join(', ')}`, why: `Меньше ${LOW_REVIEWS} новых за ${RARE_DAYS} дней при небольшом числе отзывов или оценке ниже 4.9 — ей не на чем расти`, to: STATS.reviews });
  if (avg != null && pp?.avg_rating != null && pp.count >= 3 && pp.avg_rating - avg >= 0.3) remarks.push({ light: 'yellow', text: `Средняя за период снизилась ${pp.avg_rating.toFixed(1)} → ${avg.toFixed(1)}`, why: `Средняя по отзывам этого периода против предыдущих ${RARE_DAYS} дней`, to: STATS.reviews });
  if (neg > 0) {
    const share = neg / Math.max(1, p.count);
    remarks.push({ light: share >= 0.35 ? 'orange' : share >= 0.2 ? 'yellow' : 'lime', text: `${neg} ${plural(neg, 'негативный', 'негативных', 'негативных')} из ${p.count}`, why: 'Отзывы на 1–3 звезды', to: STATS.reviews });
  }
  if (p.unanswered > 0) {
    const share = p.unanswered / Math.max(1, p.count);
    remarks.push({ light: share > 0.5 ? 'orange' : share > 0.3 ? 'yellow' : 'lime', text: `Без ответа ${p.unanswered}`, why: 'Ответ заведения видят все, кто читает отзывы; без ответа негатив висит как есть', to: STATS.reviews });
  }
  const reply = cur.reply;
  if (reply && reply.analyzed > 0) {
    const d = reply.axes;
    const templ = (d.liveliness.dist.copied ?? 0) + (d.liveliness.dist.template ?? 0);
    const generic = d.relevance.dist.generic ?? 0;
    const rude = d.tone.dist.rude ?? 0;
    if (rude > 0) remarks.push({ light: 'orange', text: `Грубых ответов ${rude}`, why: 'Резкие или пренебрежительные ответы заведения', to: STATS.reviews });
    if (templ / reply.analyzed >= 0.3) remarks.push({ light: 'yellow', text: `Шаблонных ответов ${templ} из ${reply.analyzed}`, why: 'Один и тот же текст или заготовка с именем — гость видит, что его не читали', to: STATS.reviews });
    if (generic / reply.analyzed >= 0.3) remarks.push({ light: 'lime', text: `Ответы общими словами ${generic} из ${reply.analyzed}`, why: 'Ответ не про то, что написал гость', to: STATS.reviews });
  }
  const light: Light = !p.count ? 'grey' : worstOf(remarks.map((r) => r.light));
  const problems = [...(cur.problems ?? [])].sort((a, b) => b.count - a.count).slice(0, 4);
  const ratingsText = sources.filter((x) => x.rating != null).map((x) => `${x.name} ${x.rating!.toFixed(1)}★`).join(' · ');
  const byId = new Map((cur.reviews ?? []).map((r) => [r.id, r]));
  const periodText = avg != null ? `за период средняя ${avg.toFixed(1)} по ${p.count} ${plural(p.count, 'отзыву', 'отзывам', 'отзывам')}` : `${p.count} ${plural(p.count, 'отзыв', 'отзыва', 'отзывов')} за ${RARE_DAYS} дней`;
  return {
    key: 'reviews', title: 'Отзывы', light, period: `${RARE_DAYS} дней`,
    headline: remarks.length ? `${remarks.map((r) => r.text).join(' · ')} · ${periodText}` : `${ratingsText ? `${ratingsText} · ` : ''}${periodText} · все с ответом`,
    sources,
    delta: pp && pp.count && avg != null && pp.avg_rating != null ? mkDelta(pp.avg_rating, avg, `средняя за период ${pp.avg_rating.toFixed(1)} → ${avg.toFixed(1)}`, { lowerIsBetter: false, eps: 0.05, fmt: (n) => n.toFixed(1).replace('.', ','), what: 'средняя оценка' }) : null,
    remarks, stats: { to: STATS.reviews },
    examples: [],
    details: [],
    themes: problems.map((t) => ({
      category: t.category, label: CATEGORY_LABEL[t.category] ?? t.category, count: t.count, examples: t.examples ?? [],
      reviews: t.review_ids.map((id) => byId.get(id)).filter((r): r is ReviewDto => !!r).sort((a, b) => (a.rating ?? 5) - (b.rating ?? 5)),
      to: `${STATS.reviews}&theme=problem:${encodeURIComponent(t.category)}`,
    })),
    praise: [...(cur.praise ?? [])].sort((a, b) => b.count - a.count).slice(0, 4).map((t) => ({
      category: t.category, label: CATEGORY_LABEL[t.category] ?? t.category, count: t.count, examples: t.examples ?? [],
      reviews: t.review_ids.map((id) => byId.get(id)).filter((r): r is ReviewDto => !!r).sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
      to: `${STATS.reviews}&theme=praise:${encodeURIComponent(t.category)}`,
    })),
  };
}

// ── Приход к открытию ────────────────────────────────────────────────────────

/** Вовремя — не позже стольких минут после назначенного (как карточка в Статистике). */
const ARRIVAL_LATE_MIN = 5;

/** Приход к открытию за 14 дней (Алексей 22.10, #1850): сколько дней команда была за баром к
 *  назначенному времени, сколько опоздала и насколько, когда не было никого. Фото — дни с
 *  опозданием и «никого». Функция выключена — панели нет. */
export function aggArrival(cur: StaffArrivalResponse | null | undefined, prev: StaffArrivalResponse | null | undefined): Panel | null {
  if (!cur?.enabled) return null;
  // Люди уже на первом кадре окна (съёмка стартовала позже) — «к старту съёмки были на месте»: это
  // вовремя, зелёным, без отдельной строки (Алексей 26.10 #2605/#2618).
  const counted = (r?: StaffArrivalResponse | null) => (r?.days ?? []).filter((d) => d.status !== 'pending' && (d.status !== 'no_frames' || !!d.first_seen_at));
  const days = counted(cur);
  if (!days.length) {
    return { key: 'arrival', title: 'Приход к открытию', light: 'grey', headline: 'Данных о приходе за период ещё нет', delta: null, remarks: [], examples: [], details: [], settings: { to: '/management/queues-staff', label: 'Настроить' } };
  }
  const lateOf = (ds: StaffArrivalDayDto[]) => ds.filter((d) => d.status !== 'nobody' && (d.delta_min ?? 0) > ARRIVAL_LATE_MIN);
  const late = lateOf(days);
  const nobody = days.filter((d) => d.status === 'nobody');
  const onTime = days.length - late.length - nobody.length;
  const lateAvg = late.length ? Math.round(late.reduce((s, d) => s + (d.delta_min ?? 0), 0) / late.length) : 0;
  const lateMax = late.reduce((m, d) => Math.max(m, d.delta_min ?? 0), 0);
  const badShare = (late.length + nobody.length) / days.length;
  const light: Light = nobody.length >= 2 || badShare >= 0.5 ? 'orange' : badShare >= 0.25 || lateMax >= 30 ? 'yellow' : late.length || nobody.length ? 'lime' : 'green';
  const remarks: Remark[] = [];
  const why = `Назначенное время = открытие минус «быть за N минут» из настроек. Опоздание — первые за баром позже, чем через ${ARRIVAL_LATE_MIN} мин после него.`;
  if (late.length) remarks.push({ light: lateMax >= 30 || late.length >= 3 ? 'yellow' : 'lime', text: `Позже назначенного ${late.length} из ${days.length} дн. · в среднем на ${lateAvg} мин, максимум ${lateMax} мин`, why, to: STATS.queue });
  if (nobody.length) remarks.push({ light: nobody.length >= 2 ? 'orange' : 'yellow', text: `Никого к открытию ${nobody.length} ${plural(nobody.length, 'день', 'дня', 'дней')}`, why: 'До конца окна прихода за баром никого не было видно', to: STATS.queue });
  const pDays = counted(prev);
  const pBad = pDays.length ? lateOf(pDays).length + pDays.filter((d) => d.status === 'nobody').length : null;
  return {
    key: 'arrival', title: 'Приход к открытию', light,
    headline: `Вовремя ${onTime} из ${days.length} дн.${late.length ? ` · позже ${late.length}` : ''}${nobody.length ? ` · никого ${nobody.length}` : ''}`,
    value: { value: `${late.length + nobody.length} дн.`, what: 'с опозданием' },
    delta: pBad != null ? mkDelta(pBad, late.length + nobody.length, `${pBad} → ${late.length + nobody.length} дн. с опозданием или никого`, { fmt: (n) => `${Math.round(n)} дн.`, what: 'с опозданием' }) : null,
    remarks, stats: { to: STATS.queue }, settings: { to: '/management/queues-staff', label: 'Настроить' },
    // Вместо списка цифр и одного примера — элемент из Статистики: все дни, фото любого дня (#1862).
    examples: [], details: [],
    arrivalCard: { from: isoDaysAgo(STATUS_DAYS), to: isoDaysAgo(1) },
  };
}

// ── Размер смен (Алексей 22.10, #1850; пока только стейджинг) ──────────────────
// Основа — та же матрица, что «Сколько сотрудников за баром при скольких гостях у кассы»
// в Статистике: минуты по парам (за баром, гостей у кассы). Сравнивать очередь по числу
// сотрудников нельзя — при 3 за баром очередь длиннее, потому что людей ставят в пик.
// Поэтому смотрим наоборот: при такой-то очереди — сколько было за баром.
// «Не хватало рук» — очередь от порога, а за баром один или никого; «лишние руки» —
// у кассы пусто, а за баром трое и больше.
const SHIFT_GUESTS_MAX = 6;   // строки 0…6+
const SHIFT_STAFF_MAX = 4;    // столбцы 0…4+
export type ShiftCellRange = { kitchenMin: number; kitchenMax: number; frontMin: number; frontMax: number };
export type ShiftsBlock = {
  rows: { guests: number; minutes: number; dist: number[]; avg: number }[];
  shortMin: number; idleMin: number; threshold: number; days: number;
  /** Диапазоны клеток матрицы — по ним грузятся кадры-доказательства. */
  shortCell: ShiftCellRange | null; idleCell: ShiftCellRange | null;
  coping: { key: string; title: string; light: Light; why?: string }[];
};
const COPING_SCORE: Record<Light, number | null> = { green: 100, lime: 75, yellow: 50, orange: 25, red: 0, grey: null };
export const COPING_LABEL: Record<Light, string> = {
  // #2149: без оценок людей («с трудом», «не справляется») — отставание по направлению.
  green: 'в норме', lime: 'почти в норме', yellow: 'небольшое отставание', orange: 'заметное отставание', red: 'сильное отставание', grey: 'нет данных',
};
/** Панели, которые расходуют время команды; речь и отзывы — про качество, не про время. */
const COPING_KEYS = ['queue', 'layout', 'tables', 'tasks', 'arrival'];

export function copingIndex(coping: ShiftsBlock['coping']): number | null {
  const sc = coping.map((c) => COPING_SCORE[c.light]).filter((x): x is number => x != null);
  return sc.length ? Math.round(sc.reduce((a, b) => a + b, 0) / sc.length) : null;
}

export function aggShifts(matrix: QueueWorkerMatrixCell[] | null | undefined, threshold: number | null | undefined, others: Panel[]): Panel | null {
  if (!matrix?.length) return null;
  const thr = threshold ?? 4;
  const days = STATUS_DAYS;
  const rows = Array.from({ length: SHIFT_GUESTS_MAX + 1 }, (_, g) => ({ guests: g, minutes: 0, dist: Array<number>(SHIFT_STAFF_MAX + 1).fill(0), avg: 0 }));
  let shortMin = 0;
  let idleMin = 0;
  for (const c of matrix) {
    const r = rows[Math.min(c.front_of_register, SHIFT_GUESTS_MAX)];
    r.minutes += c.minutes;
    r.dist[Math.min(c.kitchen, SHIFT_STAFF_MAX)] += c.minutes;
    r.avg += c.kitchen * c.minutes;
    if (c.front_of_register >= thr && c.kitchen <= 1) shortMin += c.minutes;
    if (c.front_of_register === 0 && c.kitchen >= 3) idleMin += c.minutes;
  }
  for (const r of rows) r.avg = r.minutes ? r.avg / r.minutes : 0;
  const coping = COPING_KEYS.map((k) => others.find((p) => p.key === k)).filter((p): p is Panel => !!p).map((p) => ({ key: p.key, title: p.title, light: p.light, why: p.headline }));
  const perDay = shortMin / 60 / days;
  const light: Light = perDay >= 1 ? 'orange' : perDay >= 0.5 ? 'yellow' : perDay >= 0.2 ? 'lime' : 'green';
  const idx = copingIndex(coping);
  return {
    key: 'shifts', title: 'Размер смен', light,
    headline: [
      shortMin >= 30 ? `Не хватало рук ${fmtPerDay(shortMin / days)}` : 'Рук хватало',
      // Тот же порог, что у вывода ниже (час в день) — в шапке не пишем то, чего нет в раскрытии (#2623).
      idleMin / days >= 60 ? `лишние руки ${fmtPerDay(idleMin / days)}` : null,
      idx != null ? `индекс команды ${idx} из 100` : null,
    ].filter(Boolean).join(' · '),
    delta: null, remarks: [], stats: { to: STATS.queue }, examples: [], details: [],
    value: { value: `${Math.round(shortMin / days)} мин`, what: 'без рук в день' },
    // Кадры «не хватало рук» — с одним за баром (не «никого»: там чаще пропуск детекции).
    shifts: {
      rows: rows.filter((r) => r.minutes > 0), shortMin, idleMin, threshold: thr, days, coping,
      shortCell: shortMin ? { kitchenMin: 1, kitchenMax: 1, frontMin: thr, frontMax: 99 } : null,
      idleCell: idleMin ? { kitchenMin: 3, kitchenMax: 99, frontMin: 0, frontMax: 0 } : null,
    },
  };
}

function fmtMinutesShort(min: number): string {
  if (min < 60) return `${Math.round(min)} мин`;
  const h = min / 60;
  return `${h < 10 ? h.toFixed(1) : Math.round(h)} ч`;
}
const fmtPerDay = (min: number) => `${fmtMinutesShort(min)} в день`;

// ── Визиты ───────────────────────────────────────────────────────────────────

const pctLight = (pct: number | null): Light => (pct == null ? 'grey' : pct < 50 ? 'orange' : pct < 70 ? 'yellow' : pct < 80 ? 'lime' : 'green');

export function aggVisits(visits: VisitSummaryDto[] | null | undefined, detail: VisitDetailDto | null | undefined, detailLoading: boolean): Panel | null {
  if (!visits) return null;
  const done = visits.filter((v) => !v.is_draft && v.total != null).sort((a, b) => (a.visit_time < b.visit_time ? 1 : -1));
  if (!done.length) {
    return { key: 'visits', title: 'Визиты', light: 'grey', headline: `Визитов за ${RARE_DAYS} дней не было — давно не проверяли`, delta: null, remarks: [], examples: [], details: [], period: `${RARE_DAYS} дней`, stats: { to: STATS.visits } };
  }
  const last = done[0]; const prevV = done[1];
  const score = last.total as number;
  const light: Light = score >= 90 ? 'green' : score >= 80 ? 'lime' : score >= 70 ? 'yellow' : score >= 50 ? 'orange' : 'red';
  const d = detail && detail.id === last.id ? detail : null;
  // Что именно не так — группы ниже нормы и невыполненные пункты из самого визита (#1438 доп.).
  // Группа с отрицательным коэффициентом («Вредные привычки») — 0 % лучший результат: свет и «ниже нормы» от 100 − pct.
  const groups = (d?.groups ?? []).map((g) => { const good = g.percentage == null ? null : g.formula_coefficient < 0 ? 100 - g.percentage : g.percentage; return { name: g.name, pct: g.percentage, good, light: pctLight(good) }; }).sort((a, b) => (a.good ?? 101) - (b.good ?? 101));
  const lowGroups = groups.filter((g) => g.good != null && g.good < 80);
  const failed = (d?.groups ?? []).flatMap((g) => g.parameters.filter((pr) => pr.value === false).map((pr) => ({ name: pr.name, w: pr.weight_yes }))).sort((a, b) => b.w - a.w).map((x) => x.name);
  const day = fmtDay(last.visit_time.slice(0, 10));
  const remarks: Remark[] = [];
  if (isProblem(light)) remarks.push({ light, text: `Визит ${day}: ${Math.round(score)} %${lowGroups.length ? ` · ниже нормы: ${lowGroups.map((g) => `${g.name} ${Math.round(g.pct!)} %`).join(', ')}` : ''}`, why: 'Балл визита — доля набранных весов по чек-листу проверяющего; норма от 90 %', to: `/usage/visits/${last.id}` });
  if (failed.length) remarks.push({ light: 'lime', text: `Не выполнено: ${failed.slice(0, 4).join(', ')}${failed.length > 4 ? ` и ещё ${failed.length - 4}` : ''}`, to: `/usage/visits/${last.id}` });
  return {
    key: 'visits', title: 'Визиты', light, period: `${RARE_DAYS} дней`,
    headline: remarks.length ? remarks.map((r) => r.text).join(' · ') : `Визит ${day}: ${Math.round(score)} % · всего визитов ${done.length}`,
    delta: prevV && prevV.total != null ? mkDelta(prevV.total, score, `${Math.round(prevV.total)} % → ${Math.round(score)} %`, { lowerIsBetter: false, eps: 1, fmt: (n) => `${Math.round(n)} %`, what: 'по визиту' }) : null,
    remarks, stats: { to: STATS.visits },
    examples: [],
    details: [],
    visit: { id: last.id, day, author: last.author?.username ?? '', total: score, groups, failed, loading: detailLoading && !d },
  };
}

// ── Часы работы: везде одно и то же (Алексей 07.10, #1297) ───────────────────
// Источники — сайт сети (через плагин сети) и карты (Яндекс, 2ГИС, через отзывы);
// сверка хранится в ZoneWorkingHoursCheck и обновляется ночью. Расхождение ТОЛЬКО у
// нас (сайт и карты согласны между собой) — не замечание клиенту, а наша правка по
// алерту хелпера (#1438 доп.): панель зелёная, в деталях видно, что поправить.
const SRC_WITH: Record<string, string> = { brand_site: 'сайтом', yandex: 'Яндекс Картами', '2gis': '2ГИС' };
const SRC_ON: Record<string, string> = { brand_site: 'на сайте', yandex: 'на Яндекс Картах', '2gis': 'в 2ГИС' };

/** День недели → «08:00–22:00»; так сравниваем расписания и сливаем одинаковые дни. */
function dayTimes(rows: SiteHoursRow[]): Map<number, string> {
  const m = new Map<number, string>();
  for (const r of rows) for (const d of maskBits(r.weekdays_mask)) m.set(d, `${r.open_time.slice(0, 5)}–${r.close_time.slice(0, 5)}`);
  return m;
}
function sameTimes(a: Map<number, string>, b: Map<number, string>): boolean {
  for (let d = 0; d < 7; d++) if ((a.get(d) ?? '') !== (b.get(d) ?? '')) return false;
  return true;
}
/** «Пн–Пт 08:00–22:00; Сб–Вс 08:00–22:00» → «Пн–Вс 08:00–22:00»: на сайте разнесено
 *  на два периода, по сути одно расписание. */
function fmtRows(rows: SiteHoursRow[]): string {
  const labels = getWeekdayLabels('ru');
  const m = dayTimes(rows);
  const parts: string[] = [];
  let d = 0;
  while (d < 7) {
    const t = m.get(d);
    if (!t) { d += 1; continue; }
    let e = d;
    while (e + 1 < 7 && m.get(e + 1) === t) e += 1;
    parts.push(`${e > d ? `${labels[d]}–${labels[e]}` : labels[d]} ${t}`);
    d = e + 1;
  }
  return parts.join('; ') || '—';
}

export function aggHours(data: HoursChecksDto | undefined): Panel | null {
  if (!data || data.checks.length === 0) return null;
  const readable = data.checks.filter((c) => c.status !== 'unreadable' && c.status != null && c.rows.length);
  const unreadable = data.checks.filter((c) => c.status === 'unreadable' || c.status === null);
  const ours = dayTimes(data.ours);
  const externalsAgree = readable.length > 0 && readable.every((c) => sameTimes(dayTimes(c.rows), dayTimes(readable[0].rows)));
  const oursDiffers = readable.length > 0 && !sameTimes(ours, dayTimes(readable[0].rows));
  const names = (cs: typeof readable) => cs.map((c) => SRC_WITH[c.source] ?? c.name).join(' и ');
  const remarks: Remark[] = [];
  let light: Light = 'green'; let headline: string;
  if (data.ours.length === 0) {
    light = 'lime'; headline = 'У нас часы работы не заданы';
    remarks.push({ light, text: 'У нас часы работы не заданы', why: 'Без часов не считаются открытие, закрытие и приход сотрудников', to: '/management/general' });
  } else if (readable.length === 0) {
    light = 'grey'; headline = 'Не удалось сверить';
  } else if (data.sync_disabled && readable.some((c) => !sameTimes(ours, dayTimes(c.rows)))) {
    // Синк отключён (Алексей 27.10): ночь не ставит часы как на сайте/картах, значит расхождение
    // само не уйдёт — висит проблемой, пока источники не поправят (или не включат синк).
    light = 'orange';
    const off = readable.filter((c) => !sameTimes(ours, dayTimes(c.rows)));
    headline = `Часы работы расходятся с ${names(off)} (синк отключён): у нас ${fmtRows(data.ours)}, ${off.map((c) => `${SRC_ON[c.source] ?? c.name} ${fmtRows(c.rows)}`).join(', ')}`;
    remarks.push({
      light, text: headline,
      why: `Гость видит часы на сайте и картах и может прийти к закрытым дверям — поправьте их там${data.sync_note ? `. Почему синк отключён: ${data.sync_note}` : ''}`,
      to: '/management/general',
    });
  } else if (!externalsAgree) {
    light = 'orange';
    headline = `Расходятся между собой: ${readable.map((c) => `${SRC_NAME[c.source] ?? c.name} ${fmtRows(c.rows)}`).join(' · ')}`;
    remarks.push({ light, text: headline, why: 'Гость видит разные часы на сайте и на картах — приходит к закрытым дверям', to: '/management/general' });
  } else if (oursDiffers) {
    // Кто прав — неизвестно: на Питстопе 26.10 верно было у нас, а сайт и карты врали (Алексей #2515). Поэтому
    // не «поправим сами», а вопрос клиенту — гость видит часы на сайте и картах.
    light = 'yellow';
    headline = `${readable.map((c) => SRC_NAME[c.source] ?? c.name).join(' и ')}: ${fmtRows(readable[0].rows)} · у нас ${fmtRows(data.ours)}`;
    remarks.push({ light, text: `${headline} — какие верные?`, why: 'Если верно у нас — поправьте на сайте и картах: гость ориентируется на них. Если там — поправьте у нас в настройках', to: '/management/general' });
  } else {
    headline = `Совпадают с ${names(readable)}: ${fmtRows(data.ours)}`;
  }
  if (unreadable.length && readable.length) headline += ` · ${unreadable.map((c) => SRC_NAME[c.source] ?? c.name).join(', ')}: не удалось сверить`;
  const details = [
    { label: 'У нас', value: fmtRows(data.ours), to: '/management/general', hint: 'График работы филиала' },
    ...(data.sync_disabled ? [{ label: 'Синк с сайтом и картами', value: `отключён${data.sync_note ? ` — ${data.sync_note}` : ''}`, to: '/management/general', hint: 'Включается в Общих настройках под графиком работы' }] : []),
    ...data.checks.map((c) => ({
      label: SRC_NAME[c.source] ?? c.name,
      value: c.status === 'unreadable' || c.status === null ? 'не удалось сверить' : fmtRows(c.rows),
      href: c.source_url || undefined,
      hint: c.source === 'brand_site' ? 'Открыть сайт' : 'Открыть карту',
    })),
  ];
  return { key: 'hours', title: 'Часы работы', light, headline, delta: null, remarks, examples: [], details, period: 'сейчас', settings: { to: '/management/general', label: 'Настроить' } };
}

// Чеки (Алексей 22.10, #1941): сколько чеков и средний чек — справочно, без «проблем».
const rub = (v: number) => money(`${Math.round(v).toLocaleString('ru-RU')} ₽`);
export function aggReceipts(cur: ReceiptsSummaryDto | undefined, prev: ReceiptsSummaryDto | undefined): Panel | null {
  if (!cur) return null;
  const days = Math.max(1, cur.by_day.filter((d) => d.count > 0).length);
  if (cur.count === 0) {
    return { key: 'receipts', title: 'Чеки', light: 'grey', headline: 'Чеков за 4 недели нет', delta: null, remarks: [], examples: [], details: [], period: '4 недели', settings: { to: '/management/receipts', label: 'Настроить' } };
  }
  // «Финансы» одним числом (Алексей 26.10 #2510): выручка в день и средний чек; динамика — по выручке в день,
  // подробности (плитки к прошлым 4 неделям) — в раскрытии, тот же FinanceBlock, что наверху статуса филиала.
  const perDay = Math.round(cur.count / days);
  const revDay = cur.revenue / days;
  const prevDays = prev?.count ? Math.max(1, prev.by_day.filter((d) => d.count > 0).length) : 0;
  const kRub = (v: number) => money(`${Math.round(v / 1000).toLocaleString('ru-RU')} тыс ₽`);
  const avg = cur.avg_check != null ? rub(cur.avg_check) : '—';
  const base = prevDays ? mkDelta(prev!.revenue / prevDays, revDay, `выручка в день ${kRub(prev!.revenue / prevDays)} → ${kRub(revDay)}`, { lowerIsBetter: false, eps: revDay * 0.01, fmt: kRub }) : null;
  const delta = base ? { ...base, what: 'в день' } : null;
  return {
    key: 'receipts', title: 'Финансы', light: 'green',
    headline: `средний чек ${avg} · ${perDay} чеков в день`,
    delta, value: { value: kRub(revDay), what: 'в день' }, cell: `${kRub(revDay)} · ${avg}`, cellTip: `Выручка в день ${kRub(revDay)} · средний чек ${avg}`, remarks: [], examples: [], details: [], finance: true,
    period: '4 недели',
    settings: { to: '/management/receipts', label: 'Настроить' },
  };
}

// Возвраты (Алексей 25.10, #2372: «большое количество возвратов — отдельный параметр, вкупе с отзывами»).
// По сети в октябре 2026 — 0,1–0,25 % чеков (LeFort 0,47 %), отсюда пороги.
// Норма в тексте — наша константа (порог жёлтого), не живые данные других филиалов (Алексей 28.10 #3121).
const RETURNS_PCT = { yellow: 0.35, orange: 0.6, red: 1.0 };
const fmtReturnPct = (n: number) => `${n.toFixed(2).replace('.', ',')}\u00a0%`;
export function aggReturns(cur: ReceiptsSummaryDto | undefined, prev: ReceiptsSummaryDto | undefined): Panel | null {
  if (!cur || !cur.count) return null;
  const pct = (cur.returns / cur.count) * 100;
  const pp = prev?.count ? (prev.returns / prev.count) * 100 : null;
  const light: Light = pct >= RETURNS_PCT.red ? 'red' : pct >= RETURNS_PCT.orange ? 'orange' : pct >= RETURNS_PCT.yellow ? 'yellow' : 'green';
  const remarks: Remark[] = light === 'green' ? [] : [{
    light, text: `Возвратов ${fmtReturnPct(pct)} чеков — норма до ${fmtReturnPct(RETURNS_PCT.yellow)}`,
    why: 'Частые возвраты — гостю что-то не понравилось или кассир ошибается при пробитии', to: '/management/analytics?tab=receipts',
  }];
  return {
    key: 'returns', title: 'Возвраты', light,
    headline: cur.returns ? `${cur.returns} ${plural(cur.returns, 'возврат', 'возврата', 'возвратов')} · ${fmtReturnPct(pct)} чеков` : 'Возвратов не было',
    delta: pp != null && (cur.returns || prev?.returns) ? mkDelta(pp, pct, `${fmtReturnPct(pp)} → ${fmtReturnPct(pct)} чеков с возвратом`, { eps: 0.05, fmt: fmtReturnPct, what: 'чеков' }) : null,
    value: { value: String(cur.returns), what: plural(cur.returns, 'возврат', 'возврата', 'возвратов') },
    remarks, examples: [],
    details: [{ label: 'Возвратов', value: String(cur.returns) }, { label: 'Доля чеков', value: fmtReturnPct(pct) }],
    period: '4 недели',
    stats: { to: '/management/analytics?tab=receipts' },
  };
}

/** План заказа (Алексей 27.10 #2972): чего не хватило вчера и сегодня — по звуку у кассы, отзывам и голосовым
 *  «Линза, добавь в план заказа». Справочно: жёлтый, когда есть что учесть в заказе, зелёный — когда всего хватало. */
export function aggOrderPlan(d: OrderPlanDto | undefined): Panel | null {
  if (!d) return null;
  const n = d.items.length;
  const top = d.items.slice(0, 3).map((it) => (it.count > 1 ? `${it.name} ×${it.count}` : it.name)).join(', ');
  const voice = d.items.filter((it) => it.kinds.includes('voice')).length;
  return {
    key: 'order_plan', title: 'План заказа', light: n ? 'yellow' : 'green',
    headline: n ? `Не хватило: ${top}${n > 3 ? ` и ещё ${n - 3}` : ''}${voice ? ` · голосом попросили ${voice}` : ''}` : 'Гостям всего хватало',
    delta: null, value: { value: String(n), what: plural(n, 'позиция', 'позиции', 'позиций') },
    remarks: [], examples: [], details: [], period: 'вчера и сегодня', orderPlan: d,
    stats: { to: STATS.order_plan },
  };
}

/** Финансовый результат наверху Статуса (Алексей 24.10 #2280, 25.10 #2371): «когда доступен средний чек,
 *  выручка и т.п. — вверх, как результат, а ниже — что поможет его улучшать». Период — 4 полные недели
 *  (≈30 дней), чтобы будни и выходные сравнивались с прошлыми 4 неделями честно. */
const FIN_DAYS = RECEIPTS_DAYS;
type FinTotals = { revenue: number; count: number; revDay: number; cntDay: number };
const finTotals = (d: ReceiptsSummaryDto | undefined): FinTotals | null => {
  if (!d?.connected || !d.count) return null;
  const days = Math.max(1, d.by_day.filter((x) => x.count > 0).length);
  return { revenue: d.revenue, count: d.count, revDay: d.revenue / days, cntDay: d.count / days };
};
// По группе — сумма по филиалам: дневные выручка и чеки складываются, средний чек = вся выручка ÷ все чеки.
const finSum = (xs: (FinTotals | null)[]): FinTotals | null => {
  const ok = xs.filter((x): x is FinTotals => !!x);
  const sum = (k: keyof FinTotals) => ok.reduce((a, x) => a + x[k], 0);
  return ok.length ? { revenue: sum('revenue'), count: sum('count'), revDay: sum('revDay'), cntDay: sum('cntDay') } : null;
};
const perDay = (t: FinTotals) => ({ rev: t.revDay, cnt: t.cntDay, avg: t.revenue / t.count });

function FinChange({ cur, prev }: { cur: number; prev: number | null | undefined }) {
  if (!prev) return null;
  const ch = (cur - prev) / prev;
  const flat = Math.abs(ch) < 0.01;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium tabular-nums', flat ? 'text-muted-foreground' : ch > 0 ? 'text-success' : 'text-destructive')}>
      {!flat && (ch > 0 ? <ArrowUp className="size-3" strokeWidth={2.5} /> : <ArrowDown className="size-3" strokeWidth={2.5} />)}
      {flat ? 'без изменений' : `${ch > 0 ? '+' : '−'}${Math.abs(ch * 100).toFixed(1).replace('.', ',')} %`}
    </span>
  );
}

/** zones — один филиал (Статус филиала) или все филиалы группы (Статус группы, Алексей 26.10 #2503): итог
 *  по группе сверху и по строке на филиал с чеками. */
export function FinanceBlock({ zones, bare = false }: { zones: { id: string; name: string }[]; bare?: boolean }) {
  const key = zones.map((z) => z.id).join(',');
  const q = useSWR(['status:fin', key], () => Promise.all(zones.map((z) => Promise.all([
    getReceiptsSummary(z.id, isoDaysAgo(FIN_DAYS), isoDaysAgo(1)).then((r) => r.data).catch(() => undefined),
    getReceiptsSummary(z.id, isoDaysAgo(FIN_DAYS * 2), isoDaysAgo(FIN_DAYS + 1)).then((r) => r.data).catch(() => undefined),
  ]).then(([c, p]) => ({ ...z, cur: finTotals(c), prev: finTotals(p) })))), { revalidateOnFocus: false });
  if (q.isLoading) return <div className="h-28 animate-pulse rounded-xl bg-muted" />;
  const rows = (q.data ?? []).filter((r) => r.cur);
  const c = finSum(rows.map((r) => r.cur));
  if (!c) return null;
  const p = finSum(rows.map((r) => r.prev));
  const C = perDay(c), P = p ? perDay(p) : null;
  const tiles = [
    { label: 'Выручка в день', value: rub(C.rev), sub: `${rub(c.revenue)} за 4 недели`, cur: C.rev, prev: P?.rev },
    { label: 'Средний чек', value: rub(C.avg), sub: 'выручка ÷ чеки', cur: C.avg, prev: P?.avg },
    { label: 'Чеков в день', value: Math.round(C.cnt).toLocaleString('ru-RU'), sub: `${c.count.toLocaleString('ru-RU')} за 4 недели`, cur: C.cnt, prev: P?.cnt },
  ];
  const group = zones.length > 1;
  return (
    // bare — внутри раскрытой панели «Финансы»: без своей рамки и заголовка, они уже есть у панели.
    <div className={bare ? '' : 'rounded-xl border bg-card px-4 py-3 shadow-sm'}>
      {!bare && <Link to="/management/analytics?tab=receipts" className="mb-2 flex items-center gap-2 text-sm font-semibold hover:underline">
        <Wallet className="size-4 text-muted-foreground" />Финансовый результат
        <span className="text-xs font-normal text-muted-foreground">
          последние 4 недели · к предыдущим 4{group && ` · ${rows.length} из ${zones.length} филиалов с чеками`}
        </span>
      </Link>}
      <div className="grid gap-3 sm:grid-cols-3">
        {tiles.map((t) => (
          <div key={t.label} className="min-w-0">
            <div className="text-[11px] text-muted-foreground">{t.label}</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tracking-tight tabular-nums">{t.value}</span>
              <FinChange cur={t.cur} prev={t.prev} />
            </div>
            <div className="text-[11px] text-muted-foreground">{t.sub}</div>
          </div>
        ))}
      </div>
      {group && rows.length > 1 && (
        <div className="mt-3 border-t pt-2">
          <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,11rem)] gap-3 pb-1 text-[11px] text-muted-foreground">
            <span>Филиал</span><span>Выручка в день</span><span>Средний чек</span><span>Чеков в день</span>
          </div>
          {rows.sort((a, b) => perDay(b.cur!).rev - perDay(a.cur!).rev).map((r) => {
            const rc = perDay(r.cur!), rp = r.prev ? perDay(r.prev) : null;
            return (
              <div key={r.id} className="grid grid-cols-[minmax(0,1fr)_repeat(3,11rem)] items-baseline gap-3 whitespace-nowrap py-1 text-sm">
                <span className="truncate">{r.name}</span>
                <span className="tabular-nums">{rub(rc.rev)} <FinChange cur={rc.rev} prev={rp?.rev} /></span>
                <span className="tabular-nums">{rub(rc.avg)} <FinChange cur={rc.avg} prev={rp?.avg} /></span>
                <span className="tabular-nums">{Math.round(rc.cnt).toLocaleString('ru-RU')} <FinChange cur={rc.cnt} prev={rp?.cnt} /></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── UI ────────────────────────────────────────────────────────────────────────

function Dot({ light, className }: { light: Light; className?: string }) {
  return <span className={cn('inline-block size-2.5 shrink-0 rounded-full', LIGHT_DOT[light], className)} aria-label={LIGHT_LABEL[light]} />;
}

/** Полоса дней: по столбику на день, цвет — уровень дня, подсказка — что было (#2298). */
export function DayStrip({ days }: { days: DayBar[] }) {
  return (
    <span className="flex h-7 items-stretch gap-[3px]">
      {days.map((b) => (
        <Tooltip key={b.day} content={b.text} side="top">
          <span className={cn('flex-1 rounded-[3px]', b.light === 'grey' ? 'bg-muted' : LIGHT_DOT[b.light])} />
        </Tooltip>
      ))}
    </span>
  );
}

/** Иконка раздела на карточке Статуса — те же, что в меню настроек функций. */
export const PANEL_ICON: Record<string, React.ElementType> = {
  layout: ShoppingBasket, queue: Users, tables: UtensilsCrossed, tasks: ListTodo, arrival: Sunrise, speech: MessageSquareText,
  reviews: Star, visits: ClipboardCheck, wifi: Wifi, receipts: ReceiptIcon, hours: Clock, shifts: UsersRound, returns: RotateCcw,
};

/** Уровень — спокойный чип с точкой, цвета системы (#2065: «цвет — уровень, а не раскраска»). */
export function LevelChip({ light, className }: { light: Light; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium', LIGHT_CHIP[light], className)}>
      <span className={cn('size-1.5 rounded-full', LIGHT_DOT[light])} />
      {LIGHT_LABEL[light]}
    </span>
  );
}

export const LIGHT_CHIP: Record<Light, string> = {
  red: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  orange: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  yellow: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  lime: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300',
  green: 'bg-success/10 text-success',
  grey: 'bg-muted text-muted-foreground',
};

/** Плашка иконки раздела в цвет уровня (#2296: «красный, зелёный — появнее»). */
const LIGHT_TILE: Record<Light, string> = {
  red: 'bg-red-100 text-red-600 ring-red-600/15 dark:bg-red-950/60 dark:text-red-400',
  orange: 'bg-orange-100 text-orange-600 ring-orange-600/15 dark:bg-orange-950/60 dark:text-orange-400',
  yellow: 'bg-amber-100 text-amber-600 ring-amber-600/15 dark:bg-amber-950/60 dark:text-amber-400',
  lime: 'bg-yellow-100 text-yellow-700 ring-yellow-600/15 dark:bg-yellow-950/60 dark:text-yellow-400',
  green: 'bg-success/15 text-success ring-success/20',
  grey: 'bg-muted text-muted-foreground ring-border',
};
/** Чип уровня на строке раздела — с обводкой, читается издалека. */
const LIGHT_CHIP_STRONG: Record<Light, string> = {
  red: 'bg-red-50 text-red-700 ring-red-600/25 dark:bg-red-950/50 dark:text-red-300',
  orange: 'bg-orange-50 text-orange-700 ring-orange-600/25 dark:bg-orange-950/50 dark:text-orange-300',
  yellow: 'bg-amber-50 text-amber-700 ring-amber-600/25 dark:bg-amber-950/50 dark:text-amber-300',
  lime: 'bg-yellow-50 text-yellow-800 ring-yellow-600/25 dark:bg-yellow-950/50 dark:text-yellow-300',
  green: 'bg-success/10 text-success ring-success/25',
  grey: 'bg-muted text-muted-foreground ring-border',
};

/** Динамика: ухудшение красится соразмерно величине — лёгкое серым, среднее жёлтым,
 *  сильное красным (#1438 п.3 «не должно быть сразу красным»). */
/** `period` — период панели («90 дней» у отзывов и визитов): сравнение идёт с предыдущим таким же периодом,
 *  подпись не должна врать «14 дней» (Алексей 21.10 #7041). */
function DeltaChip({ delta, long, period }: { delta: Delta | null; long?: boolean; period?: string }) {
  if (!delta) return null;
  const against = `Против предыдущих ${period ?? `${STATUS_DAYS} дней`}`;
  // Стрелка — куда сдвинулось значение (средний чек вырос — вверх), цвет — лучше/хуже.
  const Icon = delta.better == null ? null : (delta.up ?? !delta.better) ? ArrowUpRight : ArrowDownRight;
  const tone = delta.better == null ? 'bg-muted text-muted-foreground'
    : delta.better ? LIGHT_CHIP.green
      : delta.rel < 0.15 ? 'bg-muted text-foreground/80'
        : delta.rel < 0.4 ? LIGHT_CHIP.yellow
          : delta.rel < 0.8 ? LIGHT_CHIP.orange : LIGHT_CHIP.red;
  return (
    <Tooltip content={`${against}${delta.better === false ? ` · хуже на ${Math.round(delta.rel * 100)} %` : delta.better ? ` · лучше на ${Math.round(delta.rel * 100)} %` : ''}`}><span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs', tone)}>
      {Icon && <Icon className="size-3.5" />}
      {long ? `${delta.text} · ${against.toLowerCase()}` : delta.text}
    </span></Tooltip>
  );
}

/** Число + динамика (Алексей 23.10, #2065): «текущее число», стрелка вверх/вниз зелёная или
 *  красная, рядом мельче тем же цветом «+12 мин (к предыдущим 14 дням)», подробности — в
 *  подсказке. `compact` — для таблицы: только число со стрелкой, период — в подсказке. */
export function MetricDelta({ delta, period, compact = false, className }: { delta: Delta | null; period?: string; compact?: boolean; className?: string }) {
  if (!delta?.value) return null;
  const against = `к предыдущим ${period ?? `${STATUS_DAYS} дням`}`.replace('дней', 'дням');
  const tone = delta.better == null ? 'text-muted-foreground' : delta.better ? 'text-success' : 'text-destructive';
  // Не поменялось — значка перемены нет вовсе, не прочерк (Алексей 24.10 #2283).
  const Icon = delta.better == null ? null : delta.up ? ArrowUp : ArrowDown;
  const tip = `${delta.text} · ${against}${delta.better === false ? ` · хуже на ${Math.round(delta.rel * 100)} %` : delta.better ? ` · лучше на ${Math.round(delta.rel * 100)} %` : ''}`;
  return (
    <Tooltip content={tip} side="top">
      <span className={cn('inline-flex items-baseline gap-1.5 whitespace-nowrap', className)}>
        <span className="text-sm font-semibold tabular-nums text-foreground">{delta.value}</span>
        {!compact && delta.what && <span className="text-xs text-muted-foreground">{delta.what}</span>}
        {Icon && <Icon className={cn('size-3.5 self-center', tone)} strokeWidth={2.25} aria-hidden />}
        {!compact && Icon && <span className="text-xs tabular-nums text-muted-foreground"><span className={tone}>{delta.change}</span> {against}</span>}
      </span>
    </Tooltip>
  );
}

/** Легенда цветов: цвет — это уровень, а не раскраска (#2065). */
export function LightLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground', className)}>
      {(['red', 'orange', 'yellow', 'lime', 'green'] as Light[]).map((l) => (
        <span key={l} className="inline-flex items-center gap-1.5"><span className={cn('inline-block size-2.5 rounded-full', LIGHT_DOT[l])} />{LIGHT_LABEL[l]}</span>
      ))}
    </div>
  );
}

/** «В статистике» — кнопка, не синяя сноска (#1438 п.1). */
function StatsButton({ to, label, icon: Icon = BarChart3, variant = 'outline' }: { to: string; label?: string; icon?: React.ElementType; variant?: 'outline' | 'ghost' }) {
  return (
    <Link to={to} onClick={(e) => e.stopPropagation()} className={cn(buttonVariants({ variant, size: 'sm' }), 'h-7 shrink-0 gap-1.5 px-2.5 text-xs font-medium')}>
      <Icon className="size-3.5" />
      {label ?? 'В данных'}
    </Link>
  );
}
function StatsIcon({ to, title = 'В данных', icon: Icon = BarChart3 }: { to: string; title?: string; icon?: React.ElementType }) {
  return (
    <Link to={to} title={title} aria-label={title} onClick={(e) => e.stopPropagation()} className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-primary">
      <Icon className="size-3.5" />
    </Link>
  );
}

function WhyHint({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <Tooltip content={<span className="block max-w-64 text-xs leading-snug">{text}</span>}>
      <span className="inline-flex size-4 shrink-0 cursor-help items-center justify-center text-muted-foreground/70 hover:text-foreground"><Info className="size-3.5" /></span>
    </Tooltip>
  );
}

/** Замечания панели — конкретные строки с уровнем, «почему» под (i) и переходом. */
function Remarks({ rows }: { rows: Remark[] }) {
  if (!rows.length) return null;
  return (
    <ul className="space-y-1">
      {rows.map((r, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className={cn('mt-1 inline-block size-2 shrink-0 rounded-full', LIGHT_DOT[r.light])} />
          <span className="min-w-0 flex-1 leading-snug">{r.text}</span>
          <span className="flex shrink-0 items-center gap-0.5">
            <WhyHint text={r.why} />
            {r.to && <StatsIcon to={r.to} />}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Сетка фото-доказательств: по 2–3 в ряд, пропорции не режем, подпись целиком под
 *  фото (владелец 09.10, #1369). Клик — лайтбокс (AI186). Сверх первых шести — «показать ещё». */
/** Фото сетки с заглушкой на время загрузки и подписью, если не загрузилось (Алексей 26.10 #2525: «грузятся
 *  без лоадера, какие-то не загружаются совсем» — была пустая строка с одной подписью). */
function GridImg({ src, alt }: { src: string; alt: string }) {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  if (state === 'error') return <span className="flex h-44 w-full items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">Фото не загрузилось</span>;
  return (
    <span className="relative block">
      {state === 'loading' && <span className="block h-44 w-full animate-pulse rounded-lg bg-muted" />}
      <img src={src} alt={alt} loading="lazy" onLoad={() => setState('ok')} onError={() => setState('error')}
        className={cn('block max-h-44 w-auto max-w-full rounded-lg ring-1 ring-border/60 hover:ring-primary/50', state === 'loading' && 'absolute inset-0 opacity-0')} />
    </span>
  );
}

function PhotoGrid({ examples, cols = 3, limit = 6 }: { examples: Example[]; cols?: 2 | 3 | 4; limit?: number }) {
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const items = examples.filter((e) => e.photo?.image_url);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, limit);
  return (
    <div>
      <div className={cn('grid gap-3', cols === 4 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : cols === 3 ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}>
        {shown.map((e, i) => (
          <figure key={`${e.day}-${i}`} className="min-w-0">
            {/* Естественные пропорции кадра (#1380), с потолком по высоте — кропы стеллажей
                узкие и вытягивались на весь столбец (#1387). */}
            <button type="button" onClick={() => setOpen(i)} className="flex w-full justify-center" aria-label="Открыть фото">
              <GridImg src={e.photo!.image_url!} alt={e.caption} />
            </button>
            <figcaption className="mt-1 text-[11px] leading-snug text-muted-foreground">{e.caption}</figcaption>
          </figure>
        ))}
      </div>
      {items.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="mt-2 text-xs text-primary hover:underline">Показать ещё {items.length - shown.length}</button>
      )}
      {open != null && (
        <ImageLightbox
          srcs={items.map((e) => e.photo!.image_url!)}
          captions={items.map((e) => e.caption)}
          index={open}
          onNavigate={(n) => setOpen(n)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** Выкладка рядами (#1438 п.3): критерий → стеллажи, у которых он наблюдается; ряд
 *  раскрывается в фото именно этой проблемы; «раскрыть все» на группе. */
const DIP_QUEUE_HINT = 'Люди в зоне очереди, пока полка была ниже нормы: сумма по минутам (минуты без кадров не считаются). Одновременные просадки разных стеллажей считаются один раз.';

/** HH:MM в часах браузера — так ?time= читает Статистика (parseTimeWindow). */
const hhmmRaw = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`; };
const localDay = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
function fmtSpan(min: number): string {
  if (min < 60) return `${min} мин`;
  const h = Math.floor(min / 60); const m = min % 60;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

/** Просадки эпизодами, как столы (Алексей 21.10): три кадра стеллажа начало → середина →
 *  конец, стрелками, с явной длительностью; поток гостей в очереди за это время и кнопка
 *  «Открыть в данных» на этот стеллаж и ровно это окно. Фото не кропаются, клик — лайтбокс. */
function DipEpisodes({ episodes, zoneId }: { episodes: DipEpisode[]; zoneId: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const shown = all ? episodes : episodes.slice(0, 4);
  const flat = shown.flatMap((ep) => ep.photos.map((ph) => ({ ep, ph })));
  return (
    <div className="space-y-4">
      {shown.map((ep, ei) => {
        const day = localDay(ep.start);
        const statsTo = `/management/analytics?tab=layout&zone=${zoneId}&from=${day}&to=${day}&rack=${ep.rack_id}&time=${hhmmRaw(ep.start)}-${hhmmRaw(ep.end)}`;
        return (
          <div key={`${ep.rack_id}-${ep.start}-${ei}`} className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-y-3">
              {ep.photos.map((ph, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <StoryConnector />}
                  <StoryFrame
                    photo={{ image_url: ph.image_url, caption: '', at: ph.at }}
                    isStart={i === 0}
                    onOpen={() => setOpen(flat.findIndex((f) => f.ph === ph))}
                  />
                </React.Fragment>
              ))}
              <StoryConnector />
              <span className="flex h-24 max-w-28 shrink-0 items-center justify-center rounded border-2 border-primary bg-primary/5 px-2.5 text-center text-xs font-semibold leading-snug text-primary sm:h-28">
                ниже нормы {fmtSpan(ep.minutes)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
              <span className="font-medium text-foreground">{fmtDay(day)} · {ep.rack_name} · {hhmm(ep.start)}–{hhmm(ep.end)} · {fmtSpan(ep.minutes)}</span>
              <span className="text-muted-foreground">· не хватало ~{ep.missing_pct} % товара</span>
              <Link to={statsTo} className="inline-flex items-center gap-1 text-primary hover:underline">
                <BarChart3 className="size-3.5" />Открыть в данных
              </Link>
            </div>
            {ep.queue && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Users className="size-3.5 shrink-0" />
                <span>
                  Поток за просадку: <span className="tabular-nums text-foreground">{ep.queue.person_minutes.toLocaleString('ru-RU')} чел·мин</span> в очереди
                  {ep.queue.avg_people != null && <> · в среднем <span className="tabular-nums text-foreground">{ep.queue.avg_people.toLocaleString('ru-RU')} чел.</span> одновременно</>}
                </span>
                <WhyHint text={DIP_QUEUE_HINT} />
              </div>
            )}
          </div>
        );
      })}
      {episodes.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="text-xs text-primary hover:underline">Показать ещё {episodes.length - shown.length} эпизодов</button>
      )}
      {open != null && open >= 0 && (
        <ImageLightbox
          srcs={flat.map((f) => f.ph.image_url)}
          captions={flat.map((f) => `${f.ep.rack_name}${f.ph.at ? ` · ${hhmm(f.ph.at)}` : ''}${f.ph.fill_pct != null ? ` · выкладка ${f.ph.fill_pct} %` : ''}`)}
          index={open}
          onNavigate={(n) => setOpen(n)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** Окно для Статистики: эпизод ± 10 % его длины (не меньше минуты) — как ссылка
 *  «Самая длинная очередь» отчёта дня (checkout_control.py). */
function paddedWindow(startIso: string, endIso: string): [string, string] {
  const s = new Date(startIso).getTime(); const e = new Date(endIso).getTime();
  const pad = Math.max(60_000, (e - s) * 0.1);
  // ?time= одного дня: окно не переваливает через полночь
  const day0 = new Date(s); day0.setHours(0, 0, 0, 0);
  const lo = Math.max(day0.getTime(), s - pad);
  const hi = Math.min(day0.getTime() + 24 * 3_600_000 - 60_000, e + pad);
  return [new Date(lo).toISOString(), new Date(hi).toISOString()];
}

/** Длинные очереди сериями (Алексей 21.10, как просадки): кадры, где очередь видна, —
 *  начало → пик → конец стрелками, явное окно и длительность, пик и «спала за», кнопка
 *  «Открыть в данных» на ровно это окно. Фото не кропаются, клик — лайтбокс. */
function QueueEpisodes({ episodes, zoneId, threshold }: { episodes: QueueEpisode[]; zoneId: string; threshold: number }) {
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const shown = all ? episodes : episodes.slice(0, 4);
  const flat = shown.flatMap((ep) => ep.photos.map((ph) => ({ ep, ph })));
  return (
    <div className="space-y-4">
      {shown.map((ep, ei) => {
        const day = localDay(ep.start);
        const [lo, hi] = paddedWindow(ep.start, ep.end);
        const statsTo = `/management/analytics?tab=queue&zone=${zoneId}&from=${day}&to=${day}&time=${hhmmRaw(lo)}-${hhmmRaw(hi)}`;
        // Картинка сохраняется не у каждого кадра: бывает, что за эпизод их нет и показан ближайший кадр
        // до/после — подписываем, чтобы «3 чел.» на нём не спорило с «пик 5» (Алексей 26.10 #2508).
        const t0 = Date.parse(ep.start), t1 = Date.parse(ep.end);
        const inEp = (at: string) => { const t = Date.parse(at); return t >= t0 && t < t1; };
        const outLabel = (at: string) => { const t = Date.parse(at); return t < t0 ? ` · за ${Math.round((t0 - t) / 60000)} мин до` : t >= t1 ? ` · через ${Math.round((t - t1) / 60000)} мин после` : ''; };
        const noPeakPhoto = !ep.photos.some((ph) => inEp(ph.at));
        return (
          <div key={`${ep.start}-${ei}`} className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-y-3">
              {ep.photos.map((ph, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <StoryConnector />}
                  <StoryFrame
                    photo={{ image_url: ph.image_url, caption: '', at: ph.at }}
                    isStart={i === 0}
                    onOpen={() => setOpen(flat.findIndex((f) => f.ph === ph))}
                    action={(
                      <span className={cn('pointer-events-none rounded px-1 py-0.5 text-[10px] font-medium tabular-nums text-white', ph.is_peak && inEp(ph.at) ? 'bg-primary' : 'bg-black/60')}>
                        {ph.people} чел.{outLabel(ph.at)}
                      </span>
                    )}
                  />
                </React.Fragment>
              ))}
              {ep.photos.length > 0 && <StoryConnector />}
              <span className="flex h-24 max-w-28 shrink-0 items-center justify-center rounded border-2 border-primary bg-primary/5 px-2.5 text-center text-xs font-semibold leading-snug text-primary sm:h-28">
                очередь от {threshold} чел. {fmtSpan(ep.minutes)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
              <span className="font-medium text-foreground">{fmtDay(day)} · {hhmm(ep.start)}–{hhmm(ep.end)} · {fmtSpan(ep.minutes)}</span>
              <span className="text-muted-foreground">
                · пик {ep.peak_people} чел. в {hhmm(ep.peak_at)}{ep.drain_min != null ? ` · спала за ${ep.drain_min} мин` : ''}
                {noPeakPhoto && ep.photos.length > 0 && ' · во время очереди фото не сохранялись, показан ближайший кадр'}
              </span>
              <Link to={statsTo} className="inline-flex items-center gap-1 text-primary hover:underline">
                <BarChart3 className="size-3.5" />Открыть в данных
              </Link>
            </div>
          </div>
        );
      })}
      {episodes.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="text-xs text-primary hover:underline">Показать ещё {episodes.length - shown.length} {plural(episodes.length - shown.length, 'эпизод', 'эпизода', 'эпизодов')}</button>
      )}
      {open != null && open >= 0 && (
        <ImageLightbox
          srcs={flat.map((f) => f.ph.image_url)}
          captions={flat.map((f) => `${fmtDay(localDay(f.ep.start))} · ${hhmm(f.ph.at)} · ${f.ph.people} чел.${f.ph.is_peak ? ' · пик' : ''} · очередь ${hhmm(f.ep.start)}–${hhmm(f.ep.end)}`)}
          index={open}
          onNavigate={(n) => setOpen(n)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function LayoutGroups({ groups, zoneId }: { groups: LayoutGroup[]; zoneId: string }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const shown = groups.filter((g) => g.rows.length > 0);
  const ok = groups.filter((g) => g.rows.length === 0 && g.light !== 'grey');
  return (
    <div className="space-y-3">
      {shown.map((g) => {
        const keys = g.rows.map((r) => `${g.key}|${r.name}`);
        const allOpen = keys.every((k) => open.has(k));
        return (
          <div key={g.key}>
            <div className="mb-1.5 flex items-center gap-2">
              <Dot light={g.light} />
              <span className="text-sm font-medium">{g.title}</span>
              <WhyHint text={g.hint} />
              <span className="text-xs text-muted-foreground">{g.rows.length} {plural(g.rows.length, 'стеллаж', 'стеллажа', 'стеллажей')}</span>
              <button type="button" onClick={() => setOpen((s) => { const n = new Set(s); keys.forEach((k) => (allOpen ? n.delete(k) : n.add(k))); return n; })} className="ml-auto text-xs text-muted-foreground hover:text-foreground">
                {allOpen ? 'свернуть все' : 'раскрыть все'}
              </button>
              <StatsIcon to={STATS.layout} />
            </div>
            <div className="divide-y divide-border/60 rounded-lg border border-border/60">
              {g.rows.map((r) => {
                const k = `${g.key}|${r.name}`; const isOpen = open.has(k);
                return (
                  <div key={k}>
                    <button type="button" onClick={() => toggle(k)} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted/40">
                      <Dot light={r.light} />
                      <span className="min-w-0 flex-1 truncate">{r.name}</span>
                      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{r.metric}</span>
                      <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
                    </button>
                    {isOpen && (
                      <div className="border-t border-border/60 bg-muted/20 px-3 py-2.5">
                        {r.episodesLoading ? <SeriesSkeleton />
                          : r.episodes?.length ? <DipEpisodes episodes={r.episodes} zoneId={zoneId} />
                          : r.photos.length ? <PhotoGrid examples={r.photos} cols={4} limit={8} /> : <div className="text-xs text-muted-foreground">Фото за эти дни в отчётах нет.</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Чеки и очередь приходят отдельным запросом — пока он идёт, серая строка, а не пустота (#2528). */}
            {g.effectsLoading && !g.effects?.length && <div className="mt-1.5 h-4 w-72 animate-pulse rounded bg-muted" />}
            {!!g.effects?.length && (
              <div className="mt-1.5 space-y-0.5 px-1">
                {g.effects.map((e) => (
                  <Tooltip key={e.label} content={e.hint}><div className="flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground">
                    <span>{e.label}:</span><span className="font-medium tabular-nums text-foreground">{e.value}</span>
                  </div></Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {ok.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {ok.map((g) => <span key={g.key} className="inline-flex items-center gap-1.5"><Dot light="green" />{g.title}: в норме</span>)}
        </div>
      )}
    </div>
  );
}

/** Эпизоды столов той же цепочкой кадров, что в отчёте дня (#1380). С правом
 *  daily_report.manage — карандаш, как в карточке отчёта: исправить/убрать эпизод
 *  целиком или с конкретного фото (Алексей 21.10). Правка идёт в отчёт того дня,
 *  после неё перечитываем отчёты страницы. */
function EpisodeRows({ episodes, zoneId }: { episodes: NonNullable<Panel['episodes']>; zoneId: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const canManage = useCan('daily_report.manage', zoneId);
  const { mutate } = useSWRConfig();
  const { excludingKey, excludeError, correct } = useTableCorrection(() => {
    void mutate((key) => Array.isArray(key) && key[0] === 'analytics:status:reports');
  });
  const shown = all ? episodes : episodes.slice(0, 4);
  const groups: TableGroup[] = shown.map((ep) => ({ name: ep.caption, photos: ep.photos, minutes: ep.minutes, tableId: ep.tableId, stretchStart: ep.stretchStart }));
  const flat = groups.flatMap((g) => g.photos);
  const captions = groups.flatMap((g) => g.photos.map((ph) => `${g.name}${ph.at ? ` · ${hhmm(ph.at)}` : ''}`));
  const editable = canManage && shown.some((ep) => ep.reportId != null && ep.tableId != null);
  const onCorrect = (g: TableGroup, state: TableIncidentCorrection, cut?: TableCut) => {
    const reportId = shown[groups.indexOf(g)]?.reportId;
    if (reportId != null) void correct(reportId, g, state, cut);
  };
  return (
    <div className="space-y-3">
      {editable && (
        <div className="flex justify-end">
          <Tooltip content={t('report.day.editIncidents')} side="top">
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              aria-pressed={editing}
              aria-label={t('report.day.editIncidents')}
              className={cn(buttonVariants({ variant: editing ? 'secondary' : 'ghost', size: 'icon-sm' }))}
            >
              <Pencil className="size-3.5" />
            </button>
          </Tooltip>
        </div>
      )}
      <TableStory
        groups={groups}
        onOpen={setOpen}
        indexOf={(ph) => flat.indexOf(ph)}
        editable={editable && editing}
        excludingKey={excludingKey}
        onCorrect={onCorrect}
      />
      {excludeError && <p className="text-xs text-destructive">{excludeError}</p>}
      {episodes.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="text-xs text-primary hover:underline">Показать ещё {episodes.length - shown.length} эпизодов</button>
      )}
      {open != null && (
        <ImageLightbox
          srcs={flat.map((ph) => ph.image_url)}
          captions={captions}
          index={open}
          onNavigate={(n) => setOpen(n)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** Невыполняемые задачи: к какому часу должна была выполниться, сколько раз не сделана,
 *  «отчёты» (раскрывает выполнения за период), «изменить» (чек-лист с этой задачей) — #1438 п.2. */
function TaskRows({ rows, zoneId }: { rows: TaskRow[]; zoneId: string }) {
  const [all, setAll] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 6);
  const from = isoDaysAgo(STATUS_DAYS); const to = isoDaysAgo(1);
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Не выполняются</span>
        <span className="ml-auto normal-case tracking-normal">к какому часу · сделано из назначенных</span>
      </div>
      <div className="divide-y divide-border/60 rounded-lg border border-border/60">
        {shown.map((r) => {
          const k = `${r.text}|${r.due ?? ''}`; const isOpen = openKey === k;
          return (
            <div key={k}>
              <div className="flex items-center gap-2.5 px-3 py-2">
                <Dot light={r.light} />
                <Tooltip content={r.text}><div className="min-w-0 flex-1 text-sm leading-snug line-clamp-2">{r.text}</div></Tooltip>
                <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{r.due ? `к ${r.due}` : '—'}</span>
                <div className="hidden h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-muted sm:block"><div className="h-full rounded-full bg-emerald-400" style={{ width: `${r.total ? (r.done / r.total) * 100 : 0}%` }} /></div>
                <span className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{r.done} из {r.total}</span>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Tooltip content="Отчёты по задаче за период"><button type="button" onClick={() => setOpenKey(isOpen ? null : k)} className={cn('inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground', isOpen && 'bg-muted text-foreground')}>
                    отчёты <ChevronDown className={cn('size-3 transition-transform', isOpen && 'rotate-180')} />
                  </button></Tooltip>
                  {r.ids.length > 0 && <StatsIcon to={`/management/checklists?item=${r.ids[0]}`} title="Изменить задачу в чек-листе" icon={Pencil} />}
                  <StatsIcon to={STATS.tasks} />
                </div>
              </div>
              {isOpen && (r.ids.length ? <ChecklistItemReports zoneId={zoneId} checklistItemIds={r.ids} from={from} to={to} /> : <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">Эта задача не привязана к чек-листу — показать её отчёты нечем.</div>)}
            </div>
          );
        })}
      </div>
      {rows.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="mt-1.5 text-xs text-primary hover:underline">Показать ещё {rows.length - shown.length}</button>
      )}
    </div>
  );
}

const PROVIDER_SHORT: Record<string, string> = { yandex: 'Яндекс', '2gis': '2ГИС', linza: 'QR' };

/** «Что хвалят» — свёрнуто по умолчанию, чтобы не спорить с жалобами за внимание (#1849). */
function PraiseSpoiler({ rows }: { rows: NonNullable<Panel['praise']> }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />
        Что хвалят <span className="normal-case tracking-normal">· {rows.map((r) => r.label).join(', ')}</span>
      </button>
      {open && <div className="mt-1.5"><ThemeRows rows={rows} title="" /></div>}
    </div>
  );
}

/** Темы жалоб: строка раскрывается на месте — формулировки из разборов и сами отзывы
 *  (худшие сверху), дальше — в Статистику с уже выбранной темой (#1384). */
function ThemeRows({ rows, title = 'О чём жалуются' }: { rows: NonNullable<Panel['themes']>; title?: string }) {
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [fullRv, setFullRv] = useState<Set<number | string>>(new Set());
  if (!rows.length) return null;
  return (
    <div>
      {title && <div className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">{title}</div>}
      <div className="divide-y divide-border/60 rounded-lg border border-border/60">
        {rows.map((r) => {
          const open = openCat === r.category;
          return (
            <div key={r.category}>
              <div className="flex items-center gap-2 pr-2">
                <button type="button" onClick={() => setOpenCat(open ? null : r.category)} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left hover:bg-muted/40">
                  <span className="min-w-0 flex-1 text-sm">{r.label}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{r.count} {plural(r.count, 'отзыв', 'отзыва', 'отзывов')}</span>
                  <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
                </button>
                <StatsIcon to={r.to} title="Отзывы темы в Данных" />
              </div>
              {open && (
                <div className="space-y-2 border-t border-border/60 bg-muted/20 px-3 py-2.5">
                  {r.examples.length > 0 && (
                    <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                      {r.examples.slice(0, 4).map((ex, i) => <li key={i}>{ex}</li>)}
                    </ul>
                  )}
                  {r.reviews.slice(0, 3).map((rv) => (
                    <div key={rv.id} className="rounded-md bg-background px-3 py-2 text-sm ring-1 ring-border/60">
                      <div className="mb-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{rv.rating != null ? `${rv.rating}★` : '—'}</span>
                        <span>{PROVIDER_SHORT[rv.provider] ?? rv.provider}</span>
                        <span>{fmtDay(rv.published_at)}</span>
                        {rv.author_name && <span className="truncate">{rv.author_name}</span>}
                        {/* Ссылка на сам отзыв на площадке (Алексей 28.10 #3058). */}
                        {rv.url && (
                          <a href={rv.url} target="_blank" rel="noreferrer" className="ml-auto inline-flex shrink-0 items-center gap-1 text-primary hover:underline">
                            открыть <ExternalLink className="size-3" />
                          </a>
                        )}
                      </div>
                      {/* Отзыв целиком по клику (Алексей 28.10 #3057). */}
                      <Tooltip content={fullRv.has(rv.id) ? 'Свернуть' : 'Развернуть отзыв'}><button type="button" onClick={() => setFullRv((prev) => { const n = new Set(prev); if (n.has(rv.id)) n.delete(rv.id); else n.add(rv.id); return n; })}
                        className={cn('block w-full whitespace-pre-line text-left leading-snug', !fullRv.has(rv.id) && 'line-clamp-3')}>{rv.text}</button></Tooltip>
                    </div>
                  ))}
                  <StatsButton to={r.to} label="Все отзывы темы" variant="ghost" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Оценка на картах по источникам — плитка на источник с цветом по порогам (#1382). */
function SourceTiles({ rows }: { rows: SourceRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {rows.map((r) => (
        <div key={r.name} className={cn('rounded-lg px-3 py-2 ring-1', r.light === 'green' || r.light === 'grey' ? 'bg-muted/50 ring-transparent' : LIGHT_CHIP[r.light], r.light !== 'green' && r.light !== 'grey' && 'ring-current/20')}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Dot light={r.light} />
            <span>{r.name}</span>
            {r.href && <a href={r.href} target="_blank" rel="noreferrer" className="ml-auto text-muted-foreground hover:text-primary"><ExternalLink className="size-3.5" /></a>}
          </div>
          <div className="mt-0.5 text-sm font-medium tabular-nums">
            {r.rating != null ? `${r.rating.toFixed(1)}★` : '—'}
            {r.start != null && r.rating != null && Math.abs(r.start - r.rating) >= 0.05 && <span className="ml-1.5 text-xs font-normal text-muted-foreground">было {r.start.toFixed(1)}</span>}
          </div>
          <div className={cn('text-xs', r.low ? 'text-lime-700 dark:text-lime-300' : 'text-muted-foreground')}>
            {r.count != null ? `${r.count} ${plural(r.count, 'отзыв', 'отзыва', 'отзывов')} · ` : ''}+{r.added} за {RARE_DAYS} дн.{r.low ? ' · прирост низкий' : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Речь: плитки исходов и конкретные проблемы с «послушать» (#1438 п.4). */
function SpeechBlock({ data }: { data: NonNullable<Panel['speech']> }) {
  const navigate = useNavigate();
  const [openKind, setOpenKind] = useState<string | null>(null);
  const [more, setMore] = useState<Set<string>>(new Set());
  const openConv = (id: number) => navigate(`${STATS.speech}&conv=${id}`);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {data.bars.map((b) => (
          <div key={b.id} className={cn('rounded-lg px-3 py-2 ring-1', isProblem(b.light) ? cn(LIGHT_CHIP[b.light], 'ring-current/20') : 'bg-muted/50 ring-transparent')}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Dot light={b.light} />
              <span className="truncate">{b.title}</span>
              <span className="ml-auto"><StatsIcon to={b.to} /></span>
            </div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className="text-lg font-semibold tabular-nums">{Math.round(b.share * 100)} %</span>
              <span className="text-xs">{b.badLabel}</span>
            </div>
            <div className="text-xs text-muted-foreground">{b.n} из {data.analyzed} {plural(data.analyzed, 'разговора', 'разговоров', 'разговоров')}</div>
          </div>
        ))}
      </div>
      {data.kinds.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">Проблемы в разговорах</div>
          <div className="divide-y divide-border/60 rounded-lg border border-border/60">
            {data.kinds.map((k) => {
              const open = openKind === k.value;
              const items = more.has(k.value) ? k.items : k.items.slice(0, 3);
              return (
                <div key={k.value}>
                  <div className="flex items-center gap-2 pr-2">
                    <button type="button" onClick={() => setOpenKind(open ? null : k.value)} className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left hover:bg-muted/40">
                      <span className="shrink-0 text-sm font-medium">{k.label[0].toUpperCase()}{k.label.slice(1)}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{k.count}</span>
                      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{k.subjects.map(([s, n]) => `${s} ${n}`).join(' · ')}</span>
                      <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
                    </button>
                    <StatsIcon to={k.to} />
                  </div>
                  {open && (
                    <div className="divide-y divide-border/40 border-t border-border/60 bg-muted/20 px-3">
                      {items.length === 0 && <div className="py-2 text-xs text-muted-foreground">Примеров с записью нет.</div>}
                      {items.map((p) => {
                        const head = (
                          <div className="text-xs">
                            <span className="font-medium">{p.subject}</span>
                            {p.text && <span className="text-muted-foreground"> — {p.text}</span>}
                            {(p.requested || p.answer) && (
                              <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 text-[11px] text-muted-foreground">
                                {p.requested && <><span>Просили</span><span className="text-foreground/80">{p.requested}</span></>}
                                {p.answer && <><span>Ответ</span><span className="text-foreground/80">{p.answer}</span></>}
                              </div>
                            )}
                          </div>
                        );
                        return p.clip ? (
                          <ClipExample key={`${p.conversation_id}-${p.clip.t}`} clip={p.clip} onOpen={openConv}>{head}</ClipExample>
                        ) : (
                          <div key={p.conversation_id} className="py-1.5">
                            {head}
                            <button type="button" onClick={() => openConv(p.conversation_id)} className="mt-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">к разговору <ArrowUpRight className="size-3" /></button>
                          </div>
                        );
                      })}
                      {k.items.length > items.length && (
                        <button type="button" onClick={() => setMore((s) => new Set(s).add(k.value))} className="py-2 text-xs text-primary hover:underline">Ещё {k.items.length - items.length}</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Визит: группы с баллом и невыполненные пункты — сжато, чтобы сопоставлять с остальным. */
function VisitBlock({ v }: { v: VisitBrief }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>Визит {v.day}{v.author ? ` · ${v.author}` : ''}</span>
        <span className="font-medium text-foreground">{Math.round(v.total)} %</span>
        <Link to={`/usage/visits/${v.id}`} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'ml-auto h-7 gap-1.5 px-2.5 text-xs')}><ClipboardCheck className="size-3.5" />Открыть визит</Link>
      </div>
      {v.loading && <div className="text-xs text-muted-foreground">Загружаем подробности визита…</div>}
      {v.groups.length > 0 && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {v.groups.map((g) => (
            <div key={g.name} className={cn('rounded-lg px-3 py-2 ring-1', isProblem(g.light) ? cn(LIGHT_CHIP[g.light], 'ring-current/20') : 'bg-muted/50 ring-transparent')}>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Dot light={g.light} /><span className="truncate">{g.name}</span></div>
              <div className="mt-0.5 text-sm font-medium tabular-nums">{g.pct != null ? `${Math.round(g.pct)} %` : '—'}</div>
            </div>
          ))}
        </div>
      )}
      {v.failed.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Не выполнено</div>
          <ul className="list-disc space-y-0.5 pl-4 text-sm">
            {v.failed.slice(0, 8).map((f) => <li key={f}>{f}</li>)}
            {v.failed.length > 8 && <li className="text-muted-foreground">и ещё {v.failed.length - 8}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

const TILE_PANELS = new Set(['queue', 'tables', 'visits']);

function Details({ panel }: { panel: Panel }) {
  if (!panel.details.length) return null;
  const Extra = ({ d }: { d: Panel['details'][number] }) => (
    <>
      {d.href && <Tooltip content={d.hint}><a href={d.href} target="_blank" rel="noreferrer" aria-label={d.hint} className="text-muted-foreground hover:text-primary"><ExternalLink className="size-3.5" /></a></Tooltip>}
      {d.to && <StatsIcon to={d.to} title={d.hint ?? 'Открыть'} icon={Settings2} />}
    </>
  );
  if (TILE_PANELS.has(panel.key)) {
    return (
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {panel.details.map((d) => (
          <div key={d.label} className="rounded-lg bg-muted/50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{d.label}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm font-medium tabular-nums"><span>{d.value}</span><Extra d={d} /></div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
      {panel.details.map((d) => (
        <React.Fragment key={d.label}>
          <dt className="text-muted-foreground">{d.label}</dt>
          <dd className="flex items-center gap-1.5 tabular-nums"><span>{d.value}</span><Extra d={d} /></dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Histogram({ rows }: { rows: NonNullable<Panel['histogram']> }) {
  if (!rows.some((h) => h.minutes > 0)) return null;
  const max = Math.max(...rows.map((x) => x.minutes), 1);
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Сколько столов стояло грязными одновременно</div>
      {rows.map((h) => (
        <div key={h.label} className="grid grid-cols-[9rem_minmax(0,1fr)_auto] items-center gap-2 text-xs">
          <span className="text-muted-foreground">{h.label}</span>
          <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-amber-300" style={{ width: `${(h.minutes / max) * 100}%` }} /></div>
          <span className="tabular-nums">{fmtHours(h.minutes / 60)}</span>
        </div>
      ))}
    </div>
  );
}

/** Итог внизу панели: сколько людей провели в прикассовой зоне, пока выкладка была ниже
 *  нормы. Приглушённо — это следствие просадок, а не отдельная проблема (#1845). */
function Callout({ c }: { c: NonNullable<Panel['callout']> }) {
  // #2333: не «чел·ч», а минуты — время визита меряется минутами; большая цифра и под ней «в день».
  const min = (n: number) => `${Math.round(n).toLocaleString('ru-RU')} мин`;
  const body = (
    <Tooltip content={c.hint}><div className="rounded-lg bg-muted/40 px-4 py-3">
      <div className="flex items-baseline gap-2">
        <Users className="size-4 shrink-0 self-center text-muted-foreground" />
        <span className="text-2xl font-semibold tracking-tight tabular-nums">{min(c.minutes)}</span>
      </div>
      <div className="mt-0.5 text-sm text-muted-foreground">{c.text}</div>
      {c.perDayMin != null && (
        <div className="mt-2 inline-flex items-baseline gap-1.5 rounded-md bg-background px-2 py-1 ring-1 ring-border/60">
          <span className="text-sm font-semibold tabular-nums">≈ {min(c.perDayMin)}</span>
          <span className="text-xs text-muted-foreground">в день</span>
        </div>
      )}
    </div></Tooltip>
  );
  return c.to ? <Link to={c.to} className="block transition-opacity hover:opacity-90">{body}</Link> : body;
}

/** Заглушка серии кадров, пока она грузится — сразу в форме серии, без одиночного фото. */
function SeriesSkeleton() {
  return (
    <div className="space-y-2" aria-label="Загружаем серии кадров">
      {/* #2333: «нужен адекватный лоадер» — видно, что идёт загрузка, а не пустые серые плашки. */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />Загружаем серии кадров…
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="flex gap-1.5">
          {[0, 1, 2, 3, 4].map((k) => (
            <div key={k} className="aspect-video w-1/5 animate-pulse rounded bg-muted" style={{ animationDelay: `${(i * 5 + k) * 90}ms` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Evidence({ panel, zoneId }: { panel: Panel; zoneId: string }) {
  const hasGrid = panel.examples.length > 0;
  const hasEp = !!panel.episodes?.length;
  const q = panel.queueEpisodes;
  if (!hasGrid && !hasEp && !q && !panel.seriesLoading) return null;
  return (
    <div className="space-y-4 border-t border-border/60 pt-3">
      {hasGrid && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Примеры</div>
          <PhotoGrid examples={panel.examples} />
        </div>
      )}
      {hasEp && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Самые долгие эпизоды</div>
          <EpisodeRows episodes={panel.episodes!} zoneId={zoneId} />
        </div>
      )}
      {panel.seriesLoading && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Самые долгие очереди</div>
          <SeriesSkeleton />
        </div>
      )}
      {q && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            Самые долгие очереди{q.total > q.items.length ? ` · ${q.items.length} из ${q.total}` : ''}
          </div>
          <QueueEpisodes episodes={q.items} zoneId={zoneId} threshold={q.threshold} />
        </div>
      )}
    </div>
  );
}

const STAFF_SHADES = ['bg-muted-foreground/15', 'bg-primary/25', 'bg-primary/45', 'bg-primary/70', 'bg-primary'];
const COPING_TEXT: Record<Light, string> = {
  green: 'text-emerald-700 dark:text-emerald-400', lime: 'text-lime-700 dark:text-lime-400', yellow: 'text-amber-700 dark:text-amber-400',
  orange: 'text-orange-700 dark:text-orange-400', red: 'text-red-700 dark:text-red-400', grey: 'text-muted-foreground',
};

/** Спойлер внутри вывода: свёрнут, раскрывается на месте (#1863: выводы сверху, доказательства под ними). */
function Spoiler({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />{label}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/** Кадры клетки матрицы «за баром × у кассы» — грузятся только при раскрытии спойлера. */
function CellPhotos({ zoneId, cell, caption }: { zoneId: string; cell: ShiftCellRange; caption: string }) {
  const { data, isLoading } = useSWR(['status:shift-cell', zoneId, cell.kitchenMin, cell.kitchenMax, cell.frontMin, cell.frontMax], () => getQueueWorkerExamples(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1), cell.kitchenMin, cell.frontMin, cell).then((r) => r.data.examples), { revalidateOnFocus: false });
  // #2149: без скелетона и без фразы «кадров нет» — страница не прыгает; есть кадры — показываем.
  if (isLoading) return null;
  const ex: Example[] = (data ?? []).map((e) => ({ day: e.at.slice(0, 10), photo: { image_url: e.image_url, at: e.at }, caption: `${fmtDay(e.at.slice(0, 10))} ${hhmm(e.at)} · ${caption}` }));
  // Бар и касса на разных камерах: пример — только кадры, снятые почти одновременно (#1445);
  // камеры сохраняют фото вразнобой, и таких пар за период может не быть.
  if (!ex.length) return null;
  return <Spoiler label="Кадры этих моментов"><PhotoGrid examples={ex} /></Spoiler>;
}

/** Доказательство «не хватало рук» (Алексей 26.10 #2623/#2625: матрица — не доказательство): сколько это
 *  отдельных моментов и какие дольше всего — время, длительность, сколько у кассы и за баром, кадр
 *  кассы и ближайший кадр бара (камеры разные, время на каждом кадре своё). */
function ShortEpisodes({ zoneId, threshold }: { zoneId: string; threshold: number }) {
  const { data } = useSWR(['status:short-episodes', zoneId, threshold], () => getQueueShortEpisodes(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1), threshold).then((r) => r.data), { revalidateOnFocus: false });
  if (!data || !data.episodes.length) return null;
  const n = data.episodes_count ?? data.episodes.length;
  const long = data.long_count ?? 0;
  const ex: Example[] = data.episodes.flatMap((e) => e.photos.map((ph) => ({
    day: e.start.slice(0, 10),
    photo: { image_url: ph.image_url, at: ph.at },
    caption: ph.what === 'front'
      ? `${fmtDay(e.start.slice(0, 10))} ${hhmm(ph.at)} · касса: до ${e.front_max} гостей`
      : `${fmtDay(e.start.slice(0, 10))} ${hhmm(ph.at)} · бар: ${e.kitchen_max ? 'один сотрудник' : 'никого'}`,
  })));
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">
        Это {n} {plural(n, 'отдельный момент', 'отдельных момента', 'отдельных моментов')}
        {long ? `; дольше 3 мин подряд — ${long} ${plural(long, 'раз', 'раза', 'раз')}, всего ${fmtMinutesShort(data.long_minutes ?? 0)}` : '; ни разу дольше 3 мин подряд'}
        {n > long ? `, остальное — всплески по 1–2 мин` : ''}.
      </div>
      <div className="max-w-md divide-y divide-border/60 rounded-lg border border-border/60 text-sm">
        {data.episodes.map((e) => (
          <div key={e.start} className="flex items-baseline gap-2 px-3 py-1.5">
            <span className="tabular-nums">{fmtDay(e.start.slice(0, 10))} {hhmm(e.start)}–{hhmm(new Date(new Date(e.end).getTime() + 60000).toISOString())}</span>
            <span className="text-xs text-muted-foreground">{e.minutes} мин · у кассы до {e.front_max}, за баром {e.kitchen_max ? 'один' : 'никого'}</span>
          </div>
        ))}
      </div>
      {ex.length > 0 && <Spoiler label="Кадры самых долгих моментов"><PhotoGrid examples={ex} cols={2} limit={10} /></Spoiler>}
    </div>
  );
}

/** Один вывод: светофор, вывод словами, цифра-обоснование, доказательства под спойлерами. */
function Finding({ light, title, why, children }: { light: Light; title: string; why: string; children?: React.ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <Dot light={light} className="mt-1.5" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{why}</div>
        {children}
      </div>
    </div>
  );
}

/** Размер смен (#1850, #1863): выводы сверху, под каждым — кадры/таблица под спойлером. */
function ShiftsView({ data, zoneId }: { data: ShiftsBlock; zoneId: string }) {
  const idx = copingIndex(data.coping);
  const perDay = (m: number) => fmtPerDay(m / data.days);
  const bad = data.coping.filter((c) => c.light === 'orange' || c.light === 'red');
  const hard = data.coping.filter((c) => c.light === 'yellow');
  const lower = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
  const shortLight: Light = data.shortMin / 60 / data.days >= 1 ? 'orange' : data.shortMin / 60 / data.days >= 0.5 ? 'yellow' : data.shortMin >= 30 ? 'lime' : 'green';
  return (
    <div className="space-y-4">
      {data.shortMin >= 30 ? (
        <Finding light={shortLight} title={`В пики не хватает второго человека за баром — ${perDay(data.shortMin)}`}
          why={`${fmtMinutesShort(data.shortMin)} за ${data.days} дн. у кассы было от ${data.threshold} гостей, а за баром один сотрудник или никого.`}>
          <ShortEpisodes zoneId={zoneId} threshold={data.threshold} />
        </Finding>
      ) : (
        <Finding light="green" title="В пики рук хватает" why={`Очередь от ${data.threshold} гостей при одном сотруднике за баром — меньше 30 мин за ${data.days} дн.`} />
      )}
      {/* #2149: «лишний человек — 25 мин в день» — мелочь и звучит упрёком; показываем от часа в день. */}
      {data.idleMin / data.days >= 60 && (
        <Finding light="lime" title={`В тихие часы за баром трое и больше — ${perDay(data.idleMin)}`}
          why={`${fmtMinutesShort(data.idleMin)} за ${data.days} дн. у кассы никого, а за баром трое и больше. Может быть подготовка или уборка.`}>
          {data.idleCell && <CellPhotos zoneId={zoneId} cell={data.idleCell} caption="у кассы никого, за баром трое и больше" />}
        </Finding>
      )}
      {data.coping.length > 0 && (
        <Finding light={bad.length ? 'orange' : hard.length ? 'yellow' : 'green'}
          title={bad.length ? `Заметное отставание: ${bad.map((c) => lower(c.title)).join(', ')}` : hard.length ? `Небольшое отставание: ${hard.map((c) => lower(c.title)).join(', ')}` : 'Всё успевают'}
          why={`Индекс команды ${idx ?? '—'} из 100 — по всему, на что уходит её время: ${data.coping.map((c) => lower(c.title)).join(', ')}.`}>
          <Spoiler label="По направлениям">
            <div className="max-w-md divide-y divide-border/60 rounded-lg border border-border/60">
              {data.coping.map((c) => (
                <a key={c.key} href={`#status-${c.key}`} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/40">
                  <Dot light={c.light} />
                  {/* Почему так — главная фраза раздела (#2623: «не хватает доказательств»). */}
                  <span className="min-w-0 flex-1"><span className="block truncate">{c.title}</span>{c.why && <span className="block truncate text-[11px] text-muted-foreground">{c.why}</span>}</span>
                  <span className={cn('whitespace-nowrap text-xs font-medium', COPING_TEXT[c.light])}>{COPING_LABEL[c.light]}</span>
                </a>
              ))}
            </div>
          </Spoiler>
        </Finding>
      )}
      <Spoiler label="Как считали: сколько за баром при скольких гостях у кассы">
        <div className="max-w-2xl">
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 gap-y-1.5 whitespace-nowrap text-sm">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">У кассы</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">За баром, доля времени</span>
            <span className="text-right text-[10px] uppercase tracking-wide text-muted-foreground/70">В среднем</span>
            <span className="text-right text-[10px] uppercase tracking-wide text-muted-foreground/70">Часов</span>
            {data.rows.map((r) => (
              <React.Fragment key={r.guests}>
                <span className={cn('tabular-nums text-muted-foreground', r.guests >= data.threshold && 'font-medium text-foreground')}>{r.guests === SHIFT_GUESTS_MAX ? `${r.guests}+` : r.guests} чел.</span>
                <Tooltip content={r.dist.map((m, k) => `${k === SHIFT_STAFF_MAX ? `${k}+` : k} за баром — ${Math.round((m / r.minutes) * 100)} %`).join('\n')} className="whitespace-pre-line"><div className="flex h-3 overflow-hidden rounded-sm">
                  {r.dist.map((m, k) => m > 0 && <span key={k} className={STAFF_SHADES[k]} style={{ width: `${(m / r.minutes) * 100}%` }} />)}
                </div></Tooltip>
                <span className="text-right tabular-nums">{r.avg.toFixed(1)}</span>
                <span className="text-right tabular-nums text-muted-foreground">{fmtMinutesShort(r.minutes)}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {STAFF_SHADES.map((c, k) => (
              <span key={k} className="inline-flex items-center gap-1"><span className={cn('inline-block size-2.5 rounded-sm', c)} />{k === 0 ? 'никого' : k === SHIFT_STAFF_MAX ? `${k}+` : k}</span>
            ))}
            <span>· за баром</span>
          </div>
          <div className="mt-2 text-[11px] leading-snug text-muted-foreground">Очередь по числу сотрудников не сравниваем: при троих за баром она длиннее, потому что людей ставят в пик. Поэтому смотрим наоборот — при такой очереди сколько было за баром.</div>
        </div>
      </Spoiler>
    </div>
  );
}

/** `embed` — панель как разворот ячейки таблицы филиалов (Алексей 25.10 #2395): без своей рамки,
 *  в заголовке — филиал, шеврон и клик по заголовку сворачивают ячейку. */
export type PanelEmbed = { zoneName: string; zoneLink: string; onCollapse: () => void };

function PanelCard({ panel, rank, zoneId, defaultOpen = false, embed }: { panel: Panel; rank: number | null; zoneId: string; defaultOpen?: boolean; embed?: PanelEmbed }) {
  const problem = isProblem(panel.light);
  // По умолчанию всё свёрнуто, свёрнутая — только цветной круг и название (Алексей #1920).
  const [ownOpen, setOpen] = useState(defaultOpen);
  const open = embed ? true : ownOpen;
  const toggle = embed ? embed.onCollapse : () => setOpen((v) => !v);
  const empty = !panel.finance && !panel.seriesLoading && !panel.groups?.length && !panel.details.length && !panel.histogram && !panel.examples.length && !panel.episodes?.length && !panel.queueEpisodes && !panel.tasks?.length && !panel.sources?.length && !panel.themes?.length && !panel.speech && !panel.visit && !panel.shifts && !panel.arrivalCard && !panel.remarks.length;
  const OpenIcon = PANEL_ICON[panel.key] ?? BarChart3;
  const byGroups = !!panel.groups?.some((g) => g.rows.length);
  // #2284 → … → #2298: строка как на статус-страницах сервисов — плашка иконки в цвет уровня, название и суть,
  // справа число и мелко динамика. Раскрытие — выпадающее: шапка та же, ниже подробности (Алексей 26.10 #2510:
  // «прочитал шапку, нажал раскрыть — а всё заменилось на другую форму»). В ячейке таблицы — то же самое.
  const d = panel.delta;
  const tone = d?.better == null ? 'text-muted-foreground' : d.better ? 'text-success' : 'text-destructive';
  const Arrow = d?.better == null ? null : d.up ? ArrowUp : ArrowDown;
  const period = (panel.period ?? `${STATUS_DAYS} дней`).replace('дней', 'дням').replace('недели', 'неделям');
  const Head = (embed ? 'div' : 'button') as React.ElementType;
  return (
    <Card id={embed ? undefined : `status-${panel.key}`}
      className={cn('scroll-mt-4 overflow-hidden', embed ? 'border-0 bg-transparent shadow-none' : 'shadow-sm transition-shadow hover:shadow-md')}>
      {/* В ячейке таблицы шапка не сворачивает — только шеврон и повторный клик по ячейке (#2510). */}
      <Head type="button" onClick={embed ? undefined : toggle} aria-expanded={open} aria-label={`${panel.title}: ${LIGHT_LABEL[panel.light]} — ${open ? 'свернуть' : 'раскрыть'}`}
        className={cn('flex w-full items-center gap-4 text-left', embed ? 'px-1 py-1' : 'px-4 py-3.5 transition-colors hover:bg-muted/30')}>
        <Tooltip content={LIGHT_LABEL[panel.light]} side="top">
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset', LIGHT_TILE[panel.light])}>
            <OpenIcon className="size-[18px]" strokeWidth={2} />
          </span>
        </Tooltip>
        {/* Слева только название: цифры — в одном месте, справа (Алексей 26.10 #2608/#2609 «2ГИС: оценка 4.6»
            слева и средняя справа). Главная фраза — в подсказке и в раскрытии. */}
        <Tooltip content={panel.headline}><span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{panel.title}</span>
        </span></Tooltip>
        <span className="flex min-w-40 shrink-0 flex-col items-end gap-1 whitespace-nowrap">
          {/* Справа всегда цифра или ничего — без «Небольшая проблема» вперемешку с числами (#2301). */}
          {(d?.value || panel.value) && (
            <Tooltip side="left" content={d?.value ? <span className="block max-w-72 whitespace-normal">{d.hint && <>{d.hint}<br /></>}{d.text} — к предыдущим {period}</span> : (panel.headline ?? '')}>
            <span className="flex flex-col items-end gap-1">
              <span className="flex items-baseline gap-1">
                <span className="text-xl font-semibold leading-none tracking-tight tabular-nums">{d?.value ?? panel.value?.value}</span>
                <span className="text-xs text-muted-foreground">{d?.value ? d.what : panel.value?.what}</span>
              </span>
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                {d?.value && Arrow ? (
                  <>
                    <Arrow className={cn('size-3', tone)} strokeWidth={2.5} aria-hidden />
                    <span className={cn('font-medium tabular-nums', tone)}>{d.change}</span>
                    <span>к пред. {period}</span>
                  </>
                ) : <span>за {panel.period ?? `${STATUS_DAYS} дней`}</span>}
              </span>
            </span>
            </Tooltip>
          )}
        </span>
        {embed
          ? <button type="button" onClick={toggle} aria-label="Свернуть" className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"><ChevronDown className="size-4 rotate-180" /></button>
          : <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />}
      </Head>
      {open && (
        <div className={cn('space-y-4', embed ? 'pt-3' : 'border-t px-4 py-3')}>
          <div className="flex flex-wrap items-center gap-2">
            {/* Период — плашкой (#2326, #2506); кнопки — здесь, а не в шапке, чтобы шапка не менялась. */}
            {/* У «Часов работы» периода нет — сверка текущая, плашку не показываем (#2515: «что за сейчас»). */}
            {panel.period !== 'сейчас' && (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-px text-[11px] text-muted-foreground">
                <CalendarDays className="size-3" />Данные за {panel.period ?? `${STATUS_DAYS} дней`}
              </span>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              {panel.settings && <StatsButton to={panel.settings.to} label={panel.settings.label} icon={Settings2} variant="ghost" />}
              {panel.stats && <StatsButton to={panel.stats.to} label={panel.stats.label} />}
              {embed && <StatsButton to={embed.zoneLink} label="Статус филиала" icon={ExternalLink} variant="ghost" />}
            </span>
          </div>
          {/* Полная суть — только если в шапке поместилась не вся (#2326: не повторять то, что ниже группами). */}
          {!panel.finance && !byGroups && !panel.remarks.length && !!panel.headline && <div className={cn('text-sm', problem ? 'font-medium' : 'text-muted-foreground')}>{panel.headline}</div>}
          {!byGroups && <Remarks rows={panel.remarks} />}
          {panel.links?.map((l) => <DataLinkBlock key={l.key} link={l} />)}
          {panel.groups && <LayoutGroups groups={panel.groups} zoneId={zoneId} />}
          {panel.finance && <FinanceBlock zones={[{ id: zoneId, name: '' }]} bare />}
          {panel.orderPlan && <OrderPlanBlock data={panel.orderPlan} period={panel.period} />}
          {panel.sources && <SourceTiles rows={panel.sources} />}
          {panel.speech && <SpeechBlock data={panel.speech} />}
          {panel.visit && <VisitBlock v={panel.visit} />}
          <Details panel={panel} />
          {panel.tasks && <TaskRows rows={panel.tasks} zoneId={zoneId} />}
          {panel.themes && <ThemeRows rows={panel.themes} />}
          {panel.praise && <PraiseSpoiler rows={panel.praise} />}
          {panel.histogram && <Histogram rows={panel.histogram} />}
          {panel.shifts && <ShiftsView data={panel.shifts} zoneId={zoneId} />}
          {panel.arrivalCard && <StaffArrivalSummaryCard zoneId={zoneId} from={panel.arrivalCard.from} to={panel.arrivalCard.to} title="По дням" className="shadow-none" />}
          {empty && <div className="text-xs text-muted-foreground">Подробностей за период нет.</div>}
          <Evidence panel={panel} zoneId={zoneId} />
          {panel.callout && <Callout c={panel.callout} />}
        </div>
      )}
    </Card>
  );
}

// Связь с другими данными (#3189): заголовок — вывод, раскрытие — случаи (отзыв + что было в тот день).
const clockMin = (m: number | undefined) => (m == null ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
type LinkItem = {
  day: string; rating?: number | null; category?: string; url?: string; quote?: string;
  missed?: string[]; missed_day?: string; missed_n?: number;
  dips?: { rack: string; start: string; end: string; missing_pct: number | null }[]; dip_day?: string;
  asked?: string; answer?: string; item?: string; gap_from?: number; gap_to?: number;
};
function DataLinkBlock({ link }: { link: DataLinkDto }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-2 text-left">
        <Link2 className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <span className="flex-1 text-sm font-medium">{link.title}</span>
        <ChevronDown className={cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-5 text-xs">
          <div className="text-muted-foreground">{link.note}</div>
          {(link.items as LinkItem[]).slice(0, 20).map((x, i) => (
            <div key={i} className="border-l-2 border-amber-300/70 pl-2">
              <div className="text-muted-foreground">
                {fmtDay(x.day)}
                {x.rating != null && <> · {x.rating}★</>}
                {x.category && <> · {x.category}</>}
                {x.url && <> · <a href={x.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">отзыв</a></>}
              </div>
              {x.quote && <div>«{x.quote}»</div>}
              {x.missed && (
                <div className="text-muted-foreground">
                  Не выполнено {x.missed_day !== x.day ? `накануне (${fmtDay(x.missed_day ?? '')})` : 'в этот день'}: {x.missed.join('; ')}
                  {(x.missed_n ?? 0) > x.missed.length ? ` и ещё ${(x.missed_n ?? 0) - x.missed.length}` : ''}
                </div>
              )}
              {x.dips && (
                <div className="text-muted-foreground">
                  Выкладка {x.dip_day !== x.day ? `накануне (${fmtDay(x.dip_day ?? '')})` : 'в этот день'}:{' '}
                  {x.dips.map((d) => `${d.rack} ${hhmm(d.start)}–${hhmm(d.end)}${d.missing_pct != null ? `, пусто ${Math.round(d.missing_pct)} %` : ''}`).join('; ')}
                </div>
              )}
              {x.asked && <div>Гость: «{x.asked}» → {x.answer || 'нет'} · по чекам «{x.item}» не продавали {clockMin(x.gap_from)}–{clockMin(x.gap_to)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** «Не настроено» — внизу страницы: каждая невключённая функция карточкой-кнопкой с
 *  коротким «что даст» и переходом в настройку (владелец 09.10, #1379). */
type SetupItem = { key: string; icon: React.ElementType; name: string; pitch: string; href: string };

function NotConfigured({ zoneId, panelKeys }: { zoneId: string; panelKeys: Set<string> }) {
  const { t } = useT();
  const { search } = useLocation();
  const { features } = useZoneFeatures(zoneId);
  if (!features) return null;
  const items: SetupItem[] = [];
  const monitoring: [string, React.ElementType, string, string][] = [
    ['layout', ShoppingBasket, 'management.plugin.layout', 'dashboard.teaser.layout.desc'],
    ['queue', Users, 'management.plugin.queue', 'dashboard.teaser.queue.desc'],
    ['tables', UtensilsCrossed, 'management.plugin.tables', 'dashboard.teaser.tables.desc'],
  ];
  for (const [key, icon, nameKey, descKey] of monitoring) {
    if (!panelKeys.has(key)) items.push({ key, icon, name: t(nameKey), pitch: t(descKey), href: '/management/zoning' });
  }
  const registry: [FeatureKey, React.ElementType, string][] = [
    ['checklists', ListTodo, '/management/checklists'],
    ['receipts', ReceiptIcon, '/management/receipts'],
    ['sales_plan', TrendingUp, '/management/sales-plan'],
    ['staff_arrival', Sunrise, '/management/general'],
    ['speech', MessageSquareText, '/management/speech'],
    ['reviews', Star, '/management/reviews'],
    ['visits', ClipboardCheck, '/management/visits'],
    ['wifi', Wifi, '/management/equipment'],
  ];
  for (const [key, icon, href] of registry) {
    const st = features[key];
    if (!st) continue;   // плагина нет в этой сборке
    const needs = st.requirement;
    const off = !st.enabled || (key === 'checklists' && !st.has_content) || needs != null;
    if (!off) continue;
    const to = needs === 'microphone' || needs === 'router' ? '/management/equipment' : needs === 'staff_polygon' ? '/management/zoning' : href;
    items.push({ key, icon, name: t(`features.${key}.name`), pitch: t(`features.${key}.pitch`), href: to });
  }
  if (!items.length) return null;
  return (
    <section className="pt-2">
      <div className="mb-2 pt-2 text-sm font-semibold text-foreground">Можно подключить</div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link key={it.key} to={{ pathname: it.href, search }} className="group flex items-start gap-3 rounded-lg border border-dashed border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-muted/40">
              <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground group-hover:text-primary" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{it.name}</div>
                <div className="mt-1 text-xs leading-snug text-muted-foreground">{it.pitch}</div>
              </div>
              <span className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0 text-xs group-hover:border-primary/50')}>{t('dashboard.teaser.configure')}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export type StatusFeatures = { tasks: boolean; speech: boolean; reviews: boolean; visits: boolean; receipts?: boolean };

/** `onlyKey` — одна панель раскрытой: так раскрывается ячейка таблицы филиалов (Алексей 25.10
 *  #2325: «то же самое, что по одному филиалу при раскрытии ряда»). */
export default function AnalyticsStatusTab({ zoneId, features, onlyKey, embed }: { zoneId: string; features: StatusFeatures; onlyKey?: string; embed?: PanelEmbed }) {
  const tasksConfigured = features.tasks;
  // Ячейка таблицы (onlyKey) грузит только данные своего раздела — раскрытие было долгим, потому что тянуло
  // весь статус филиала (Алексей 26.10 #2515).
  const want = (...keys: string[]) => !onlyKey || keys.includes(onlyKey);
  const winOn = want('layout', 'queue', 'tables', 'tasks', 'shifts');
  const speechCur = useSWR(features.speech && want('speech') ? ['status:speech-overview', zoneId, 'cur'] : null, () => getConversationsOverview(Number(zoneId), isoDaysAgo(STATUS_DAYS), isoDaysAgo(1)), { revalidateOnFocus: false });
  const speechPrev = useSWR(features.speech && want('speech') ? ['status:speech-overview', zoneId, 'prev'] : null, () => getConversationsOverview(Number(zoneId), isoDaysAgo(STATUS_DAYS * 2), isoDaysAgo(STATUS_DAYS + 1)), { revalidateOnFocus: false });
  const reviewsCur = useSWR(features.reviews && want('reviews') ? ['status:reviews', zoneId, 'cur'] : null, () => getReviewsStats(zoneId, isoDaysAgo(RARE_DAYS), isoDaysAgo(0)).then((r) => r.data), { revalidateOnFocus: false });
  const reviewsPrev = useSWR(features.reviews && want('reviews') ? ['status:reviews', zoneId, 'prev'] : null, () => getReviewsStats(zoneId, isoDaysAgo(RARE_DAYS * 2), isoDaysAgo(RARE_DAYS + 1)).then((r) => r.data), { revalidateOnFocus: false });
  const visits = useSWR(features.visits && want('visits') ? ['status:visits', zoneId] : null, () => listZoneVisits(zoneId, { from: isoDaysAgo(RARE_DAYS), to: isoDaysAgo(0) }).then((r) => r.data.results), { revalidateOnFocus: false });
  // Последний сданный визит — подробно: группы и невыполненные пункты (#1438 доп.).
  const lastVisitId = useMemo(() => visits.data?.filter((v) => !v.is_draft && v.total != null).sort((a, b) => (a.visit_time < b.visit_time ? 1 : -1))[0]?.id ?? null, [visits.data]);
  const visitDetail = useSWR(lastVisitId ? ['status:visit', lastVisitId] : null, () => getVisit(lastVisitId!).then((r) => r.data), { revalidateOnFocus: false });
  const receiptsCur = useSWR(features.receipts && want('receipts', 'returns') ? ['status:receipts', zoneId, 'cur'] : null, () => getReceiptsSummary(zoneId, isoDaysAgo(RECEIPTS_DAYS), isoDaysAgo(1)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  const receiptsPrev = useSWR(features.receipts && want('receipts', 'returns') ? ['status:receipts', zoneId, 'prev'] : null, () => getReceiptsSummary(zoneId, isoDaysAgo(RECEIPTS_DAYS * 2), isoDaysAgo(RECEIPTS_DAYS + 1)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  const hours = useSWR(want('hours') ? ['status:hours', zoneId] : null, () => getHoursChecks(zoneId).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  // План заказа — когда есть хоть один источник (микрофон или отзывы), без отдельного включения (#2972).
  // Скрыто до доработки (Алексей 27.10 #2991).
  const orderPlanOn = ORDER_PLAN_READY && (features.speech || features.reviews) && want('order_plan');
  const orderPlan = useSWR(orderPlanOn ? ['status:order-plan', zoneId] : null, () => getOrderPlan(zoneId, isoDaysAgo(1), isoDaysAgo(0)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  // Текущее окно: вчера и 13 дней до него; предыдущее — 14 дней перед ним.
  const cur = useWindow(zoneId, STATUS_DAYS, 1, winOn);
  const prev = useWindow(zoneId, STATUS_DAYS * 2, STATUS_DAYS + 1, winOn);
  const reports = useReports(zoneId, STATUS_DAYS, 1, winOn);
  const prevReports = useReports(zoneId, STATUS_DAYS * 2, STATUS_DAYS + 1, winOn);
  // Просадки эпизодами (Алексей 21.10); не блокирует панели — приходит позже.
  const dips = useSWR(want('layout') ? ['analytics:status:dips', zoneId] : null, () => getLayoutDips(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 });
  // Очереди сериями (Алексей 21.10) — так же не блокирует: до ответа панель с фото пика дня.
  // Приход к открытию (#1850): своя ручка, в status-window его нет.
  const arrivalCur = useSWR(want('arrival') ? ['status:arrival', zoneId, 'cur'] : null, () => getStaffArrival(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  const arrivalPrev = useSWR(want('arrival') ? ['status:arrival', zoneId, 'prev'] : null, () => getStaffArrival(zoneId, isoDaysAgo(STATUS_DAYS * 2), isoDaysAgo(STATUS_DAYS + 1)).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  const queueEps = useSWR(want('queue', 'shifts') ? ['analytics:status:queue-episodes', zoneId] : null, () => getQueueEpisodes(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1), QUEUE_EPISODES_TOP).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 });
  // Размер смен — только стейджинг (#1850): матрица из queue-detail, ручка тяжёлая (до ~40 с), не блокирует.
  const staging = !isProdHost();
  const queueDetail = useSWR(staging && want('shifts') ? ['status:queue-detail', zoneId] : null, () => getQueueDetail(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1), undefined, { photoCount: 1 }).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 });
  const linksOn = !onlyKey || ['tasks', 'layout', 'speech'].includes(onlyKey);
  const links = useSWR(linksOn ? ['status:data-links', zoneId] : null, () => getDataLinks(zoneId, 30).then((r) => r.data.links).catch(() => [] as DataLinkDto[]), { revalidateOnFocus: false, dedupingInterval: 10 * 60_000 });
  const panels = useMemo(() => {
    if (winOn && (!cur.data || !reports.data)) return null;
    if (!winOn && onlyKey && [['speech', speechCur], ['reviews', reviewsCur], ['visits', visits], ['receipts', receiptsCur], ['returns', receiptsCur], ['hours', hours], ['arrival', arrivalCur]]
      .some(([k, q]) => k === onlyKey && (q as { isLoading: boolean }).isLoading)) return null;
    const c = cur.data ?? [];
    const p = prev.data ?? [];
    const r = reports.data ?? [];
    const pr = prevReports.data ?? [];
    const base = [
      want('layout') ? aggLayout(c, p, r, pr, dips.data, dips.isLoading) : null, want('queue') ? aggQueue(c, p, queueEps.data, queueEps.isLoading) : null,
      want('arrival') ? aggArrival(arrivalCur.data, arrivalPrev.data) : null, want('tables') ? aggTables(c, p, r, pr) : null, want('tasks') ? aggTasks(c, p, tasksConfigured) : null,
      features.speech && want('speech') ? aggSpeech(speechCur.data, speechPrev.data) : null,
      features.reviews && want('reviews') ? aggReviews(reviewsCur.data, reviewsPrev.data) : null,
      features.visits && want('visits') ? aggVisits(visits.data, visitDetail.data, visitDetail.isLoading) : null,
      features.receipts && want('receipts') ? aggReceipts(receiptsCur.data, receiptsPrev.data) : null,
      features.receipts && want('returns') ? aggReturns(receiptsCur.data, receiptsPrev.data) : null,
      want('hours') ? aggHours(hours.data) : null,
      orderPlanOn ? aggOrderPlan(orderPlan.data) : null,
    ].filter((x): x is Panel => x != null);
    const LINK_PANEL: Record<string, string> = { checklists: 'tasks', layout: 'layout', speech: 'speech' };
    for (const l of links.data ?? []) {
      const pnl = base.find((x) => x.key === LINK_PANEL[l.panel]);
      if (pnl) pnl.links = [...(pnl.links ?? []), l];
    }
    const shifts = staging ? aggShifts(queueDetail.data?.worker_matrix, queueEps.data?.threshold, base) : null;
    return (shifts ? [...base, shifts] : base).sort((a, b) => LIGHT_ORDER[a.light] - LIGHT_ORDER[b.light]);
  }, [staging, queueDetail.data, cur.data, prev.data, reports.data, prevReports.data, dips.data, dips.isLoading, queueEps.data, queueEps.isLoading, arrivalCur.data, arrivalPrev.data, tasksConfigured, features, speechCur.data, speechPrev.data, reviewsCur.data, reviewsPrev.data, visits.data, visitDetail.data, visitDetail.isLoading, hours.data, receiptsCur.data, receiptsPrev.data, onlyKey, winOn, speechCur.isLoading, reviewsCur.isLoading, visits.isLoading, receiptsCur.isLoading, hours.isLoading, arrivalCur.isLoading, orderPlanOn, orderPlan.data, links.data]);

  // Ошибка при уже показанных данных — оставить их (обновятся сами); без данных — общий экран (#3224).
  if (cur.error && !cur.data) return <ServerStatusNotice variant="card" error={cur.error} />;
  if (onlyKey) {
    const one = panels?.find((p) => p.key === onlyKey);
    if (!panels) return <div className="h-24 animate-pulse rounded-lg bg-muted" />;
    return one ? <PanelCard panel={one} rank={null} zoneId={zoneId} defaultOpen embed={embed} /> : <div className="py-3 text-sm text-muted-foreground">Раздел у этого филиала не подключён.</div>;
  }
  if (!panels) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  }
  // С финблоком наверху панель «Чеки» в списке дублировала бы его — в списке остаётся то, что на него влияет.
  const listed = features.receipts ? panels.filter((p) => p.key !== 'receipts') : panels;
  const problems = listed.filter((p) => isProblem(p.light));
  const rest = listed.filter((p) => !problems.includes(p));
  const SectionLabel = ({ children, count }: { children: React.ReactNode; count?: number }) => (
    <div className="flex items-center gap-2 pt-2 text-sm font-semibold text-foreground">
      {children}
      {count != null && count > 0 && <span className="rounded-full bg-muted px-1.5 py-px text-[11px] font-medium tabular-nums text-muted-foreground">{count}</span>}
    </div>
  );
  return (
    <div className="space-y-4">
      {features.receipts && <FinanceBlock zones={[{ id: zoneId, name: '' }]} />}
      {/* Шапка — как у остальных разделов, без краткой сводки (#1438). Общего периода нет —
          у каждого блока свой, он подписан на панели (#1378). */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel count={problems.length || undefined}>{problems.length ? 'Над чем работать' : panels.length ? 'Всё в норме' : 'Пока ничего не настроено'}</SectionLabel>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {prev.isLoading && <span>считаем динамику…</span>}
          <Link to="/management/status/all" className="text-primary hover:underline">Все филиалы →</Link>
        </div>
      </div>
      <div className="space-y-2">
        {problems.map((p, i) => <PanelCard key={p.key} panel={p} rank={i + 1} zoneId={zoneId} />)}
      </div>
      {rest.length > 0 && (
        <>
          {problems.length > 0 && <SectionLabel count={rest.length}>Без замечаний</SectionLabel>}
          <div className="space-y-2">
            {rest.map((p) => <PanelCard key={p.key} panel={p} rank={null} zoneId={zoneId} />)}
          </div>
        </>
      )}
      <NotConfigured zoneId={zoneId} panelKeys={new Set(panels.map((p) => p.key))} />
    </div>
  );
}
